import { appStore } from '../core/store';
import type { PlaybackMode, SongRecord, MarkerNotes, MarkerNoteFormat, MarkerNoteTab, AudioFileFormat } from '../core/types';
import {
  DEFAULT_MARKER_LEAD_IN_SECONDS,
  DEFAULT_METRONOME_SETTINGS,
  MARKER_LEAD_IN_OPTIONS,
  MARKER_LEAD_IN_BEAT_OPTIONS,
  BUILT_IN_NOTE_TABS,
  allNoteTabs,
  noteTabLabel,
  markerNoteText,
  markerNoteFormat,
  markerNoteSheet,
} from '../core/types';
import { formatBytes } from '../core/format';
import { getStorageInfo, requestPersistentStorage } from '../core/storageInfo';
import { confirmDialog, promptDialog, selectDialog } from '../ui/confirm';
import { showToast } from '../ui/toast';
import { database } from '../db/database';
import { markerEditorPlayer, type MarkerPlayerEvent } from '../audio/MarkerEditorPlayer';
import {
  decodeWaveformPeaks,
  drawWaveform,
  drawWaveformWindow,
  computeBeatTicks,
  drawBeatTicks,
  nearestBeatTime,
  adjacentBeatTime,
  pickupBeatsFromFirstBeatTime,
  type WaveformPeaks,
} from '../audio/waveform';
import { ICON_PLAY, ICON_PAUSE, ICON_RESTART } from '../ui/transportIcons';
import { effectiveShortLabel, secondsPerBeat } from '../core/markerFormat';
import {
  MAX_NOTE_SHEET_COLUMNS,
  MAX_NOTE_SHEET_ROWS,
  parseSheetClipboard,
  renderSheetTable,
  sheetClipboardExceedsLimits,
  sheetHasContent,
} from '../ui/noteSheet';
import { applyNoteZoom, renderNoteZoomControl, stepNoteZoom } from '../ui/noteZoom';
import { buildSingleFormatNotePatch, noteFormatHasContent, noteTextHasContent } from '../ui/noteContent';
import { authStore } from '../core/authStore';
import { supabaseConfigured } from '../core/supabaseClient';
import { listMyBands, type MyBand } from '../core/bandsApi';
import { syncSongToBand } from '../core/songSync';

function formatMarkerTime(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/** Parses "m:ss" (or a bare number of seconds) back into seconds. Returns
 * null for anything unparseable so the caller can ignore a bad edit rather
 * than silently corrupting a marker's timestamp. */
function parseMarkerTime(value: string): number | null {
  const trimmed = value.trim();
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    const minutes = Number(colonMatch[1]);
    const seconds = Number(colonMatch[2]);
    if (seconds >= 60) return null;
    return minutes * 60 + seconds;
  }
  if (/^\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  return null;
}

const STATUS_LABEL: Record<SongRecord['status'], string> = {
  ok: 'OK',
  missing: 'MISSING FILE',
  unreadable: 'UNREADABLE',
  unsupported: 'UNSUPPORTED',
  'mono-warning': 'MONO FILE',
  checking: 'CHECKING…',
};

function statusPillClass(status: SongRecord['status']): string {
  if (status === 'ok') return 'pill pill-ok';
  if (status === 'mono-warning') return 'pill pill-warn';
  if (status === 'checking') return 'pill pill-neutral';
  return 'pill pill-danger';
}

/** Mobile-only compact status glyph replacing the text pill (see
 * .song-list-meta-mobile) — collapses SongStatus's values down to the 4
 * that actually change what a user does next: ok (nothing to do),
 * warning (plays fine, has a caveat — currently just mono-warning),
 * error (missing/unreadable/unsupported all mean "re-import this file" —
 * one shared icon rather than 3 near-identical ones nobody would reliably
 * tell apart at a glance), and checking (transient scan in progress). The
 * full specific reason (STATUS_LABEL) stays available via title/aria-label
 * on the icon itself. */
function statusIcon(status: SongRecord['status']): string {
  const label = STATUS_LABEL[status];
  if (status === 'ok') {
    return `<svg class="song-status-icon song-status-icon-ok" viewBox="0 0 20 20" width="18" height="18" role="img" aria-label="${label}"><title>${label}</title><circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="m6 10.5 2.5 2.5 5.5-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  if (status === 'mono-warning') {
    return `<svg class="song-status-icon song-status-icon-warn" viewBox="0 0 20 20" width="18" height="18" role="img" aria-label="${label}"><title>${label}</title><path d="M10 2.5 18 17H2Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M10 8v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="10" cy="14.5" r="1" fill="currentColor"/></svg>`;
  }
  if (status === 'checking') {
    return `<svg class="song-status-icon song-status-icon-neutral" viewBox="0 0 20 20" width="18" height="18" role="img" aria-label="${label}"><title>${label}</title><circle cx="5" cy="10" r="1.4" fill="currentColor"/><circle cx="10" cy="10" r="1.4" fill="currentColor"/><circle cx="15" cy="10" r="1.4" fill="currentColor"/></svg>`;
  }
  return `<svg class="song-status-icon song-status-icon-danger" viewBox="0 0 20 20" width="18" height="18" role="img" aria-label="${label}"><title>${label}</title><circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M10 6v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="10" cy="14" r="1" fill="currentColor"/></svg>`;
}

/** Mobile-only compact pills replacing the "Tracks + Click" text (see
 * .song-list-meta-mobile) — a small icon-based glyph here tried fitting
 * "waveform + click tick" into one shape and read as illegible noise, and
 * splitting it into two tiny icons was still hard to parse at this size.
 * Plain text pills read faster: a file-format pill (WAV/MP3, omitted for
 * Metronome Only — there's no file) plus a CLICK pill whenever the mode
 * actually has a click (tracks-click or metronome), omitted for
 * tracks-only. Neither pill implies anything is wrong — that's the
 * status icon's job (see statusIcon) — these are purely descriptive. */
function modeFormatPills(mode: PlaybackMode, fileFormat: AudioFileFormat | null): string {
  const pills: string[] = [];
  if (fileFormat) pills.push(`<span class="pill pill-neutral song-format-pill">${fileFormat.toUpperCase()}</span>`);
  if (mode === 'tracks-click' || mode === 'metronome') pills.push('<span class="pill pill-neutral song-format-pill">CLICK</span>');
  return pills.join('');
}

/** Sentinel value for the band filter bar's "Unassigned" pill — distinct
 * from `null` (which means "All bands", the filter's default/off state) and
 * from any real band id, since a song's own `bandId: null` already means
 * "no band chosen." */
const UNASSIGNED_BAND_FILTER = '__unassigned__';

function modeLabel(mode: PlaybackMode): string {
  if (mode === 'tracks-click') return 'Tracks + Click';
  if (mode === 'metronome') return 'Metronome Only';
  return 'Tracks Only';
}

export function renderLibrarySection(container: HTMLElement): () => void {
  container.innerHTML = '';

  /** The library is a scan-first list. Only one song exposes its full editor
   * at a time; null keeps every row compact so a large catalog remains easy
   * to browse. This is deliberately view-only state and is not persisted. */
  const libraryRouteTarget = window.location.hash.replace(/^#\//, '').split('/')[1];
  let expandedSongId: string | null = libraryRouteTarget ? decodeURIComponent(libraryRouteTarget) : null;

  // Cache of the signed-in user's Supabase bands (see bandsApi.ts) for the
  // "Sync to Cloud Band" action below — refetched lazily on first use per
  // mount rather than on every render, since it requires a network round
  // trip. Deliberately named "Cloud Band" everywhere user-facing, and kept
  // in a distinctly-named variable here, to avoid colliding with the
  // pre-existing, purely-local BandRecord "Band" dropdown a few lines below
  // (song.bandId / appStore.getState().bands) — an entirely different,
  // single-device-only organizing concept that predates Supabase entirely.
  let myCloudBandsCache: MyBand[] | null = null;

  // Only the expanded song exposes its mini-player/zoomed strip, and only
  // one song's audio can be loaded/playable at a time, matching both the
  // single-open-row accordion and markerEditorPlayer's own exclusivity rule.
  // This tracks WHICH song currently owns that audio;
  // null means no song's audio has been loaded yet (nothing has been
  // interacted with since the page opened, or the last loaded song's file
  // failed to load). Set by loadMarkerAudioIfNeeded, called at the top of
  // every action that needs this song's audio actually loaded.
  let loadedMarkerSongId: string | null = null;
  let isMiniPlaying = false;
  let miniCurrentTime = 0;
  let miniDuration = 0;
  let miniVirtualBeatTime: number | null = null;
  /** Peak overview for whichever song's marker editor is currently open, so
   * lining up the synthetic click against the real waveform is visual, not
   * just by ear. Cleared/recomputed per song since it's a one-time decode of
   * that song's own audio, not derived from playback state. */
  let miniWaveformPeaks: WaveformPeaks | null = null;
  let miniWaveformSongId: string | null = null;
  /** Which of the three mutually-exclusive marker-editor sub-panels is open
   * below the shared mini-player/zoomed-strip — 'markers' (place/name/edit
   * section markers), 'click' (tune a synthetic click's BPM/offset/first
   * beat), or 'countin' (choose this song's own pre-roll/marker-tap
   * lead-in). Only one at a time, same "one thing open" discipline as
   * loadedMarkerSongId itself; opening one closes the others rather than
   * stacking multiple under the single strip. null means none is open yet
   * (the expanded editor is visible, but no tab body is). Resets
   * to null whenever a different song's marker editor is opened. */
  let markerSubPanel: 'markers' | 'click' | 'countin' | null = null;
  /** Song whose full marker grid is currently open in the "Edit Markers"
   * modal — null when closed. The grid used to always render inline under
   * every open marker editor; pulling it into an on-demand modal keeps the
   * default view (zoomed strip, tuning steps, Create Markers) short even for
   * songs with many markers, since the grid is only a `document.body`-level
   * overlay while actually in use, same pattern as confirmDialog/promptDialog. */
  let editMarkersModalSongId: string | null = null;
  /** Marker ids whose Notes panel is currently expanded in the "Edit
   * Markers" grid — same tap-to-reveal discipline as expandedInfoKeys, kept
   * as its own Set (rather than folded into that one) since these track
   * genuinely editable content, not read-only info text, and closing the
   * modal (editMarkersModalSongId -> null) should reasonably reset which
   * were open rather than leaking across different songs/sessions. */
  const expandedMarkerNotes = new Set<string>();
  /** Which of the three note fields (lyrics/chords/general) is showing per
   * marker whose Notes panel is expanded — defaults to 'lyrics' the first
   * time a marker's notes are opened (the most commonly needed one per the
   * feature's own motivating case). Keyed by marker id so different
   * markers can independently be showing different tabs at once. */
  const activeNotesTabByMarker = new Map<string, keyof MarkerNotes>();
  /** Which band's songs are currently shown in the list below — null means
   * "All" (every song regardless of band, the default so this new filter
   * never hides anything for someone who hasn't set up bands yet).
   * `UNASSIGNED_BAND_FILTER` is a distinct sentinel from null: it means
   * "only songs with no band," which null (show everything) must not be
   * confused with. Purely a view concern — never persisted, never touches
   * SongRecord itself beyond reading `bandId`. */
  let activeBandFilter: string | null = null;
  /** Free-text search over song names, applied on top of (not instead of)
   * the band filter above — both narrow the same list together. Matches
   * anywhere in the name (not just the start), case-insensitive. Empty
   * string means "no search," the default. Purely a view concern, same as
   * activeBandFilter — never persisted. */
  let songSearchQuery = '';
  /** Unified tap-to-reveal "i" info panels — one Set of arbitrary string
   * keys shared by every "i" button on this screen (Import Songs' own
   * format/MP3-caveat text, each song's file-info details, and each Align
   * Beat/Mark as First Beat row's usage note), rather than a separate ad hoc
   * toggle mechanism per button. Deliberately tap-to-toggle everywhere, not
   * hover-based: this is a touch-first app (phone/tablet), and CSS :hover
   * simply never fires on touch, so a tooltip-only "i" would be permanently
   * stuck closed for the app's actual primary input method. Multiple keys
   * can be open at once — there's no reason opening one song's info should
   * close another's. */
  const expandedInfoKeys = new Set<string>();

  function renderInfoToggle(key: string, label: string): string {
    return `<button class="info-toggle-btn" data-action="toggle-info" data-info-key="${escapeHtml(key)}" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">i</button>`;
  }

  function renderInfoPanel(key: string, bodyHtml: string): string {
    return expandedInfoKeys.has(key) ? `<div class="info-panel">${bodyHtml}</div>` : '';
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div class="route-page-header library-page-header">
      <div>
        <h1>Song Library</h1>
        <p class="text-dim">Import, organize, and prepare songs for setlists and the Player.</p>
      </div>
    </div>
    <div class="card" id="storage-card"></div>
    <div class="card">
      <div class="row card-heading-row">
        <h2 style="margin:0">Import Songs</h2>
        <button class="info-toggle-btn" id="import-info-btn" title="About file formats" aria-label="About file formats">i</button>
      </div>
      <div class="info-panel" id="import-info-panel" hidden>
        <p class="text-dim" style="margin-top:0">Import stereo WAV files. Left channel = backing track (PA). Right channel = click/cues (in-ear).</p>
        <p class="text-warn" style="margin-bottom:0">
          MP3 import is also supported, but MP3 encoders can mix left/right channel data together (joint/mid-side
          stereo). This app cannot verify true channel separation for MP3 the way it can for WAV — always confirm
          by ear through the full PA/in-ear signal chain before a show. WAV is strongly recommended for anything
          with a click track.
        </p>
      </div>
      <div class="row" style="margin-top:12px">
        <button class="btn btn-primary" id="import-wav-btn">Import Audio File(s)</button>
        <button class="btn" id="add-metronome-btn">Add Metronome-Only Song</button>
      </div>
      <input type="file" id="wav-input" accept="audio/wav,audio/x-wav,.wav,audio/mpeg,.mp3" multiple style="display:none" />
    </div>
    <div class="card">
      <h2 style="margin-top:0">Songs (<span id="song-count">0</span>)</h2>
      <input type="text" id="song-search-input" class="song-search-input" placeholder="Search songs…" style="margin-bottom:12px" />
      <div class="row band-filter-bar" id="band-filter-bar" style="margin-bottom:12px"></div>
      <div class="card-list" id="song-list"></div>
    </div>
    <div id="edit-markers-modal-root"></div>
  `;
  container.appendChild(wrapper);

  const storageCard = wrapper.querySelector<HTMLDivElement>('#storage-card')!;
  const songSearchInput = wrapper.querySelector<HTMLInputElement>('#song-search-input')!;
  const bandFilterBar = wrapper.querySelector<HTMLDivElement>('#band-filter-bar')!;
  const songList = wrapper.querySelector<HTMLDivElement>('#song-list')!;
  const songCount = wrapper.querySelector<HTMLSpanElement>('#song-count')!;
  const importBtn = wrapper.querySelector<HTMLButtonElement>('#import-wav-btn')!;
  const addMetronomeBtn = wrapper.querySelector<HTMLButtonElement>('#add-metronome-btn')!;
  const wavInput = wrapper.querySelector<HTMLInputElement>('#wav-input')!;
  const editMarkersModalRoot = wrapper.querySelector<HTMLDivElement>('#edit-markers-modal-root')!;
  const importInfoBtn = wrapper.querySelector<HTMLButtonElement>('#import-info-btn')!;
  const importInfoPanel = wrapper.querySelector<HTMLDivElement>('#import-info-panel')!;

  importInfoBtn.addEventListener('click', () => {
    importInfoPanel.hidden = !importInfoPanel.hidden;
  });

  async function refreshStorageCard(): Promise<void> {
    const info = await getStorageInfo();
    const totalAudioBytes = appStore
      .getState()
      .songs.reduce((sum, s) => sum + (s.fileSizeBytes ?? 0), 0);

    storageCard.innerHTML = `
      <div class="row-between">
        <div>
          <strong>Persistent storage:</strong>
          ${
            info.persisted === null
              ? '<span class="pill pill-neutral">UNKNOWN (not supported)</span>'
              : info.persisted
                ? '<span class="pill pill-ok">GRANTED</span>'
                : '<span class="pill pill-warn">NOT GRANTED</span>'
          }
        </div>
        ${info.persisted === false ? '<button class="btn btn-small" id="request-persist-btn">Request Persistence</button>' : ''}
      </div>
      <p class="text-dim" style="margin-bottom:0">
        Approximate audio stored: ${formatBytes(totalAudioBytes)}
        ${info.quotaBytes ? ` · Browser storage used: ${formatBytes(info.usageBytes)} of ${formatBytes(info.quotaBytes)}` : ''}
      </p>
    `;
    storageCard.querySelector('#request-persist-btn')?.addEventListener('click', async () => {
      const granted = await requestPersistentStorage();
      if (granted) showToast('Persistent storage granted.', 'info');
      else showToast('Persistent storage was not granted by the browser.', 'warn');
      await refreshStorageCard();
    });
  }

  /** Millisecond step for the zoomed strip's own flanking nudge buttons —
   * one shared step regardless of what the buttons are currently nudging
   * (the mini-player's playhead when "Add Markers" is open or neither
   * sub-panel is, the synthetic click's offset when "Add Custom Click" is
   * open; see the buttons' own click handler for that context switch). */
  const STRIP_NUDGE_MS_STEP = 10;

  function formatOffsetMs(offsetSeconds: number): string {
    const ms = Math.round(offsetSeconds * 1000);
    return `${ms >= 0 ? '+' : ''}${ms}ms`;
  }

  /** For a song whose own audio has no click baked in, this lets the drummer
   * layer a generated click on top (right channel only — same safety
   * guarantee as everywhere else) to find a BPM + offset that locks onto the
   * song's real downbeat, which essentially never lands exactly on 0.000s.
   * Deliberately independent of `mode`: a Tracks + Click song already has a
   * real click baked in and wouldn't need this, but nothing stops turning it
   * on anyway if useful for editing; it's the song's own opt-in flag that
   * actually gates whether it's audible, not the mode.
   *
   * Flattened (no more numbered Step 1/2/3 framing) since it now lives under
   * its own "Add Custom Click" sub-carat rather than sharing space with
   * "Create Markers" — the numbering existed to visually separate steps
   * crammed alongside marker controls, which is no longer necessary now that
   * the two are entirely separate sub-panels. Positioning the playhead
   * itself (nudge by ms/beat) happens via the shared flanking nudge stacks
   * next to the zoomed strip, not controls owned by this panel. */
  function renderSyntheticClickSection(song: SongRecord, isThisSongLoaded: boolean): string {
    const enabled = song.syntheticClickEnabled;
    const pickupBeats = pickupBeatsFromFirstBeatTime(song.firstBeatTimeSeconds, song.metronomeSettings, song.syntheticClickOffsetSeconds);

    return `
      <div class="synthetic-click-panel" id="song-panel-click-${song.id}" role="region" aria-label="Add Custom Click">
        <label class="synthetic-click-enable-row">
          <input type="checkbox" class="synthetic-click-toggle" data-song-id="${song.id}" ${enabled ? 'checked' : ''} />
          <span><strong>Add Custom Click</strong>${enabled ? '' : '<small>Generate a click when the file does not contain one.</small>'}</span>
        </label>
        ${
          enabled
            ? `<div class="synthetic-click-controls">
                <div class="click-tune-row">
                  <label class="click-setting-field">
                    <span>BPM</span>
                    <input type="number" class="synthetic-click-bpm" min="30" max="300"
                      value="${song.metronomeSettings.bpm}" data-song-id="${song.id}" />
                  </label>
                  <label class="click-setting-field">
                    <span>Subdivision</span>
                    <select class="synthetic-click-subdivision" data-song-id="${song.id}">
                      <option value="1" ${song.metronomeSettings.subdivision === 1 ? 'selected' : ''}>Quarter notes</option>
                      <option value="2" ${song.metronomeSettings.subdivision === 2 ? 'selected' : ''}>Eighth notes</option>
                      <option value="4" ${song.metronomeSettings.subdivision === 4 ? 'selected' : ''}>Sixteenth notes</option>
                    </select>
                  </label>
                </div>

                <div class="click-tune-grid" style="margin-top:12px">
                  <div class="click-tune-action">
                    <div class="click-tune-action-main">
                      <button class="btn btn-ready" data-action="mark-as-aligned" data-song-id="${song.id}" ${!isThisSongLoaded ? 'disabled' : ''}>Align Beat</button>
                      ${renderInfoToggle(`align-beat:${song.id}`, 'About Align Beat')}
                    </div>
                    <div class="click-tune-action-result">
                      <span class="click-tune-state synthetic-click-offset-value${song.beatOneReferenceSeconds === null ? ' click-tune-state-unset' : ''}">${formatOffsetMs(song.syntheticClickOffsetSeconds)}</span>
                      ${
                        song.beatOneReferenceSeconds !== null
                          ? `<button class="btn btn-small btn-icon-only" data-action="clear-beat-one-reference" data-song-id="${song.id}" title="Clear alignment">
                              <svg viewBox="0 0 24 24" width="16" height="16"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
                            </button>`
                          : ''
                      }
                    </div>
                    ${renderInfoPanel(`align-beat:${song.id}`, `<p class="text-dim" style="margin:0">Nudge the strip until the click locks to the beat, then tap Align Beat — that beat becomes "1" for the numbered ticks.</p>`)}
                  </div>

                  <div class="click-tune-action">
                    <div class="click-tune-action-main">
                      <button class="btn btn-ready" data-action="mark-first-beat" data-song-id="${song.id}" ${!isThisSongLoaded ? 'disabled' : ''}>Mark as First Beat</button>
                      ${renderInfoToggle(`mark-first-beat:${song.id}`, 'About Mark as First Beat')}
                    </div>
                    <div class="click-tune-action-result">
                      <span class="click-tune-state${song.firstBeatTimeSeconds === null ? ' click-tune-state-unset' : ''}">${song.firstBeatTimeSeconds === null ? 'Not marked yet' : `${pickupBeats} pickup beat${pickupBeats === 1 ? '' : 's'}`}</span>
                      ${
                        song.firstBeatTimeSeconds !== null
                          ? `<button class="btn btn-small btn-icon-only" data-action="clear-first-beat" data-song-id="${song.id}" title="Clear marked first beat">
                              <svg viewBox="0 0 24 24" width="16" height="16"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
                            </button>`
                          : ''
                      }
                    </div>
                    ${renderInfoPanel(`mark-first-beat:${song.id}`, `<p class="text-dim" style="margin:0">Nudge close to the song's real first beat, then tap Mark as First Beat — the pre-roll count-in skips exactly that many pickup beats.</p>`)}
                  </div>
                </div>
              </div>`
            : ''
        }
      </div>
    `;
  }

  /** A thin strip directly under the mini-player's scrub track showing where
   * each of the song's markers falls — a small tick at the marker's exact
   * time position (same left% math as the scrub fill) plus its short code
   * underneath, so markers are visible at a glance while scrubbing/playing,
   * not just in the list below. Clicking a tick jumps the mini-player there,
   * same as the "jump" button in the marker list rows. */
  function renderMiniMarkerRail(song: SongRecord, durationSeconds: number): string {
    if (song.markers.length === 0 || durationSeconds <= 0) return '';
    return `
      <div class="mini-marker-rail">
        ${song.markers
          .map(
            (m) => `
          <button class="mini-marker-tick" style="left:${(m.timeSeconds / durationSeconds) * 100}%"
            data-action="jump-marker" data-song-id="${song.id}" data-marker-id="${m.id}"
            title="${escapeHtml(m.label || 'Untitled marker')} (${formatMarkerTime(m.timeSeconds)})">
            <span class="mini-marker-tick-line"></span>
            <span class="mini-marker-tick-label">${effectiveShortLabel(m)}</span>
          </button>`,
          )
          .join('')}
      </div>
    `;
  }

  /** A single tick ABOVE the mini-player's scrub track marking the song's
   * marked first-beat position (`firstBeatTimeSeconds`), mirroring
   * `renderMiniMarkerRail`'s ticks below it — so the one point the pre-roll
   * count-in actually depends on is visible right on the main waveform, not
   * just as text in the tuning panel below. Distinct from a real
   * SongMarker (it's a single derived point, not a user-managed list), so
   * it gets its own small rail rather than joining the marker rail itself. */
  function renderMiniFirstBeatMark(song: SongRecord, durationSeconds: number): string {
    if (song.firstBeatTimeSeconds === null || durationSeconds <= 0) return '';
    const leftPercent = (song.firstBeatTimeSeconds / durationSeconds) * 100;
    return `
      <div class="mini-first-beat-rail">
        <button class="mini-first-beat-tick" style="left:${leftPercent}%"
          data-action="jump-first-beat" data-song-id="${song.id}"
          title="1st Beat (${formatMarkerTime(song.firstBeatTimeSeconds)})">
          <span class="mini-first-beat-tick-label">1st</span>
          <span class="mini-first-beat-tick-line"></span>
        </button>
      </div>
    `;
  }

  /** One side (left = negative, right = positive) of the zoomed strip's
   * flanking nudge stack: an ms-step button above a beat-step button,
   * stacked vertically to match the strip's own height. What the ms button
   * actually nudges depends on which sub-panel is open (see the shared
   * "nudge-strip" action handler): the mini-player's playhead when "Add
   * Markers" is open or neither sub-panel is, the synthetic click's offset
   * when "Add Custom Click" is open — one pair of buttons whose meaning
   * follows context, rather than two separate parallel nudge UIs. While
   * "Add Custom Click" is open, the ms button is ALSO disabled once Align
   * Beat has already been used (`beatOneReferenceSeconds !== null`): nudging
   * the offset further has no visible effect on the strip's numbering until
   * Clear Alignment resets it, so leaving it enabled would invite a nudge
   * that looks like it did nothing. The beat button always walks the
   * playhead one whole beat on the click grid (there's no equivalent "one
   * beat of offset" concept), so it's disabled whenever there's no click
   * grid to snap to yet, regardless of alignment state. */
  function renderNudgeStack(song: SongRecord, isThisSongLoaded: boolean, direction: 1 | -1): string {
    const sign = direction > 0 ? '+' : '−';
    const msDisabled =
      markerSubPanel === 'click' ? song.beatOneReferenceSeconds !== null : !isThisSongLoaded;
    const beatDisabled = !isThisSongLoaded || !song.syntheticClickEnabled;
    return `
      <div class="beat-alignment-nudge-stack">
        <button class="btn btn-small" data-action="nudge-strip" data-song-id="${song.id}"
          data-ms="${direction * STRIP_NUDGE_MS_STEP}" ${msDisabled ? 'disabled' : ''}>
          ${sign}${STRIP_NUDGE_MS_STEP}ms
        </button>
        <button class="btn btn-small" data-action="nudge-strip-beat" data-song-id="${song.id}"
          data-beats="${direction}" ${beatDisabled ? 'disabled' : ''}>
          ${sign}1 beat
        </button>
      </div>
    `;
  }

  /** Produces the mini-player/zoomed-strip/sub-panel-toggles inside a song's
   * expandable editor body. Only the CURRENTLY LOADED song's card shows the
   * mutually-exclusive sub-panel bodies (Add Markers / Add Custom Click);
   * markerSubPanel is a single session-wide variable rather than per-song
   * state, since only one song's audio is ever active at a time anyway — an
   * unloaded song's card shows the toggles themselves (so clicking one is
   * always possible, and doubles as this song's own "load my audio" trigger)
   * but not their contents. */
  function renderMarkersSection(song: SongRecord): string {
    const isThisSongLoaded = markerEditorPlayer.currentSongId === song.id;
    const playing = isThisSongLoaded && isMiniPlaying;
    const curTime = isThisSongLoaded ? miniCurrentTime : 0;
    const dur = isThisSongLoaded ? miniDuration : song.durationSeconds ?? 0;
    const activeSubPanel = isThisSongLoaded ? markerSubPanel : null;
    const hasMarkers = song.markers.length > 0;
    const hasCustomClick = song.syntheticClickEnabled;
    const hasCountIn = song.markerLeadInBeats !== null || song.markerLeadInSeconds !== DEFAULT_MARKER_LEAD_IN_SECONDS;

    return `
      <div class="mini-player" data-song-id="${song.id}">
        <div class="mini-player-row">
          <button class="mini-play-btn" data-action="mini-play-pause" data-song-id="${song.id}">
            ${playing ? ICON_PAUSE : ICON_PLAY}
          </button>
          <button class="mini-restart-btn" data-action="mini-restart" data-song-id="${song.id}" title="Back to 0:00">
            ${ICON_RESTART}
          </button>
          <div class="mini-scrub-wrap">
            ${renderMiniFirstBeatMark(song, dur)}
            <div class="mini-scrub-track" data-action="mini-scrub" data-song-id="${song.id}">
              <canvas class="mini-waveform-canvas"></canvas>
              <div class="mini-scrub-fill" style="width:${dur > 0 ? (curTime / dur) * 100 : 0}%"></div>
              ${!isThisSongLoaded && song.mode !== 'metronome' ? '<span class="mini-scrub-idle-label">Loading…</span>' : ''}
            </div>
            ${renderMiniMarkerRail(song, dur)}
          </div>
          <div class="mini-time">${formatMarkerTime(curTime)} / ${formatMarkerTime(dur)}</div>
        </div>
      </div>
      <div class="beat-alignment-row">
        <div class="beat-alignment-strip" data-song-id="${song.id}">
          <canvas class="beat-alignment-canvas"></canvas>
          <div class="beat-alignment-start-label"></div>
          <div class="beat-alignment-first-beat-label"></div>
          <div class="beat-alignment-marker-labels"></div>
        </div>
        <div class="beat-alignment-controls">
          <span class="beat-alignment-controls-label">Nudge</span>
          ${renderNudgeStack(song, isThisSongLoaded, -1)}
          ${renderNudgeStack(song, isThisSongLoaded, 1)}
        </div>
      </div>

      <div class="marker-sub-toggles${activeSubPanel === null ? ' marker-sub-toggles-last' : ''}" role="group" aria-label="Song editing panels">
        <button class="marker-sub-toggle${activeSubPanel === 'markers' ? ' is-active' : ''}${hasMarkers ? ' has-content' : ''}" data-action="toggle-add-markers" data-song-id="${song.id}"
          aria-expanded="${activeSubPanel === 'markers'}" aria-controls="song-panel-markers-${song.id}">
          <span>Add Markers</span>${hasMarkers ? '<span class="marker-sub-toggle-dot" aria-hidden="true"></span>' : ''}
        </button>
        <button
          class="marker-sub-toggle${activeSubPanel === 'click' ? ' is-active' : ''}${hasCustomClick ? ' has-content' : ''}"
          data-action="toggle-add-click"
          data-song-id="${song.id}"
          aria-expanded="${activeSubPanel === 'click'}"
          aria-controls="song-panel-click-${song.id}"
          ${song.fileFormat === 'mp3' ? 'disabled title="Not available for MP3 — browsers cannot seek MP3 precisely enough for a click to stay in sync. Re-import this song as WAV to use a custom click."' : ''}
        >
          <span>Add Custom Click</span>${hasCustomClick ? '<span class="marker-sub-toggle-dot" aria-hidden="true"></span>' : ''}
        </button>
        <button class="marker-sub-toggle${activeSubPanel === 'countin' ? ' is-active' : ''}${hasCountIn ? ' has-content' : ''}" data-action="toggle-count-in" data-song-id="${song.id}"
          aria-expanded="${activeSubPanel === 'countin'}" aria-controls="song-panel-countin-${song.id}">
          <span>Count-in</span>${hasCountIn ? '<span class="marker-sub-toggle-dot" aria-hidden="true"></span>' : ''}
        </button>
      </div>

      ${
        activeSubPanel === 'markers'
          ? `<div class="marker-place-panel" id="song-panel-markers-${song.id}" role="region" aria-label="Add Markers">
              <button class="btn btn-ready add-marker-btn" data-action="add-marker-here" data-song-id="${song.id}" ${!isThisSongLoaded ? 'disabled' : ''}>
                Set Marker Here
              </button>
              <div class="marker-list-summary">
                <span class="text-dim">
                  ${song.markers.length === 0 ? 'No markers yet.' : `${song.markers.length} marker${song.markers.length === 1 ? '' : 's'}.`}
                </span>
              </div>
              <div class="marker-list marker-list-inline">
                ${renderMarkerListRows(song)}
              </div>
            </div>`
          : ''
      }
      ${activeSubPanel === 'click' ? renderSyntheticClickSection(song, isThisSongLoaded) : ''}
      ${activeSubPanel === 'countin' ? renderCountInSection(song) : ''}
    `;
  }

  /** Per-song lead-in picker — used for BOTH the pre-roll count-in at song
   * start and marker-tap seeks mid-song (see SongRecord.markerLeadInSeconds's
   * own comment; one field, two consumers, same as before this became
   * per-song). Beat-count options (4/8/12/16 — 1 to 4 measures of 4/4) only
   * show for a song with a synthetic click enabled, since only then is BPM
   * known; otherwise the plain-seconds picker is the only option, matching
   * the picker's original behavior when it lived in Performance mode. */
  function renderCountInSection(song: SongRecord): string {
    const options = song.syntheticClickEnabled
      ? MARKER_LEAD_IN_BEAT_OPTIONS.map(
          (beats) =>
            `<button class="lead-in-option${beats === song.markerLeadInBeats ? ' is-selected' : ''}" data-action="set-count-in-beats" data-song-id="${song.id}" data-beats="${beats}">${beats}ct</button>`,
        ).join('')
      : MARKER_LEAD_IN_OPTIONS.map(
          (seconds) =>
            `<button class="lead-in-option${song.markerLeadInBeats === null && seconds === song.markerLeadInSeconds ? ' is-selected' : ''}" data-action="set-count-in-seconds" data-song-id="${song.id}" data-seconds="${seconds}">${seconds}s</button>`,
        ).join('');

    return `
      <div class="count-in-panel" id="song-panel-countin-${song.id}" role="region" aria-label="Count-in">
        <div class="row" style="margin:0 0 8px; gap:6px; align-items:center">
          <span class="text-dim">Count-in duration</span>
          ${renderInfoToggle(`count-in:${song.id}`, 'About Count-in')}
        </div>
        ${renderInfoPanel(`count-in:${song.id}`, `<p class="text-dim" style="margin:0">How long to count in before this song starts (Play), or before a tapped marker (mid-song) — only applies while the active setlist is in Rehearsal mode.</p>`)}
        <div class="lead-in-picker-grid">${options}</div>
      </div>
    `;
  }

  /** Renders the actual editable grid of a song's markers — shared by the
   * "Edit Markers" modal (its only home now; the grid used to sit inline
   * under every open marker editor, which meant scrolling past a full table
   * even for songs with many markers just to reach nothing below it).
   *
   * Tab labels are no longer a fixed 3-entry object here — see
   * noteTabLabel/allNoteTabs in core/types.ts, which fold in whatever
   * CustomNoteTab entries currently exist (appStore.getState().settings.
   * customNoteTabs) alongside the 3 built-ins. Unlike Performance mode's
   * fixed "My Notes + one swappable slot" row (which had to stay capped at
   * 2 buttons — see renderNotesTabRow's own comment in performance.ts),
   * this row was already a plain, ungated button loop with no width
   * constraint, so it needed no structural redesign to grow with however
   * many tabs now exist — just a dynamic source list instead of a fixed
   * object's keys. */

  /** Whether a marker's notes have anything worth a visual hint on its
   * (collapsed) row — so a marker with real content stands out from one
   * that's never had notes touched at all, without needing to expand every
   * row just to check. Iterates whatever keys are actually present on this
   * marker (built-in or custom) rather than 3 named fields, so a custom
   * tab's content counts too. Text-only, matching this function's existing
   * (pre-custom-tabs) scope — a Sheet-format tab with content but no plain
   * text isn't reflected here, same gap as before. */
  function markerHasAnyNotes(notes: MarkerNotes): boolean {
    return Object.values(notes).some((value) => value.trim());
  }

  function renderMarkerNoteTab(song: SongRecord, marker: SongRecord['markers'][number], tab: MarkerNoteTab, activeTab: MarkerNoteTab): string {
    const hasContent = noteFormatHasContent(marker, tab, markerNoteFormat(marker, tab));
    const isCustom = !BUILT_IN_NOTE_TABS.includes(tab);
    const label = noteTabLabel(tab, appStore.getState().settings.customNoteTabs);
    if (!isCustom) {
      return `<button class="pill pill-toggle${tab === activeTab ? ' is-active' : ''}${hasContent ? ' has-content' : ''}" data-action="set-notes-tab" data-song-id="${song.id}" data-marker-id="${marker.id}" data-notes-tab="${tab}">${escapeHtml(label)}</button>`;
    }
    // Custom tabs get their own small rename/delete affordances alongside
    // the same select-this-tab pill — built-in tabs never show these,
    // they're not user-removable/renameable (see CustomNoteTab's own
    // comment for why rename keeps the tab's id, and delete leaves any
    // existing content under it inert rather than deleting it everywhere).
    return `<span class="marker-notes-tab-wrap">
      <button class="pill pill-toggle${tab === activeTab ? ' is-active' : ''}${hasContent ? ' has-content' : ''}" data-action="set-notes-tab" data-song-id="${song.id}" data-marker-id="${marker.id}" data-notes-tab="${tab}">${escapeHtml(label)}</button>
      <button type="button" class="marker-notes-tab-rename" data-action="rename-note-tab" data-song-id="${song.id}" data-note-tab-id="${tab}" title="Rename tab" aria-label="Rename ${escapeHtml(label)} tab">✎</button>
      <button type="button" class="marker-notes-tab-delete" data-action="delete-note-tab" data-song-id="${song.id}" data-note-tab-id="${tab}" title="Delete tab" aria-label="Delete ${escapeHtml(label)} tab">×</button>
    </span>`;
  }

  function renderMarkerNotesPanel(song: SongRecord, m: SongRecord['markers'][number]): string {
    if (!expandedMarkerNotes.has(m.id)) return '';
    const customNoteTabs = appStore.getState().settings.customNoteTabs;
    const activeTab = activeNotesTabByMarker.get(m.id) ?? 'lyrics';
    const activeLabel = noteTabLabel(activeTab, customNoteTabs);
    const activeFormat = markerNoteFormat(m, activeTab);
    const activeTextHasContent = noteTextHasContent(markerNoteText(m, activeTab));
    const activeSheetHasContent = sheetHasContent(markerNoteSheet(m, activeTab));
    const isEmpty = !activeTextHasContent && !activeSheetHasContent;
    const tabs = allNoteTabs(customNoteTabs)
      .map((tab) => renderMarkerNoteTab(song, m, tab, activeTab))
      .join('');
    return `
      <div class="marker-notes-panel">
        <div class="row marker-notes-tabs">
          ${tabs}
          <button type="button" class="pill marker-notes-add-tab" data-action="add-note-tab" data-song-id="${song.id}" title="Add a custom tab (e.g. Bass, Keys)">+ Tab</button>
        </div>
        <div class="note-format-row">
          <span>Format</span>
          <div class="note-format-switch" role="group" aria-label="${escapeHtml(activeLabel)} format">
            <button class="${activeFormat === 'text' ? 'is-selected' : ''}" data-action="set-notes-format" data-song-id="${song.id}" data-marker-id="${m.id}" data-notes-tab="${activeTab}" data-note-format="text">Text</button>
            <button class="${activeFormat === 'sheet' ? 'is-selected' : ''}" data-action="set-notes-format" data-song-id="${song.id}" data-marker-id="${m.id}" data-notes-tab="${activeTab}" data-note-format="sheet">Sheet</button>
          </div>
          ${
            isEmpty
              ? `<button class="note-sheet-paste-btn" data-action="set-notes-format" data-song-id="${song.id}" data-marker-id="${m.id}" data-notes-tab="${activeTab}" data-note-format="sheet">Paste Sheet</button>`
              : ''
          }
          ${renderNoteZoomControl(activeFormat, `data-song-id="${song.id}" data-marker-id="${m.id}"`)}
          ${
            activeFormat === 'sheet' && activeSheetHasContent
              ? `<button class="note-sheet-clear-btn" data-action="clear-marker-sheet" data-song-id="${song.id}" data-marker-id="${m.id}" data-notes-tab="${activeTab}">Clear Sheet</button>`
              : ''
          }
        </div>
        ${
          activeFormat === 'text'
            ? `
              ${
                isEmpty
                  ? `<div class="note-empty-hint">
                      <strong>Start typing notes</strong>
                      <button class="note-sheet-paste-btn" data-action="set-notes-format" data-song-id="${song.id}" data-marker-id="${m.id}" data-notes-tab="${activeTab}" data-note-format="sheet">Paste Sheet</button>
                    </div>`
                  : ''
              }
              <textarea class="marker-notes-textarea" placeholder="${escapeHtml(activeLabel)}…"
                data-action="edit-marker-notes" data-song-id="${song.id}" data-marker-id="${m.id}" data-notes-tab="${activeTab}"
              >${escapeHtml(markerNoteText(m, activeTab))}</textarea>`
            : `<div class="note-sheet-editor" tabindex="0" data-song-id="${song.id}" data-marker-id="${m.id}" data-notes-tab="${activeTab}">
                ${
                  activeSheetHasContent
                    ? renderSheetTable(markerNoteSheet(m, activeTab), true)
                    : `<div class="note-sheet-empty"><strong>Paste cells from Google Sheets</strong><span>Copy a range, click here, and paste. Maximum ${MAX_NOTE_SHEET_ROWS} rows × ${MAX_NOTE_SHEET_COLUMNS} columns.</span></div>`
                }
              </div>`
        }
      </div>`;
  }

  function renderMarkerListRows(song: SongRecord): string {
    if (song.markers.length === 0) {
      return '<div class="empty-markers">No markers yet. Play to the spot you want, then tap "Add Marker Here."</div>';
    }
    return song.markers
      .map(
        (m) => `
      <div class="marker-row-wrap">
        <div class="marker-row" data-marker-id="${m.id}">
          <input type="text" class="marker-time-input" value="${formatMarkerTime(m.timeSeconds)}"
            title="Timestamp (m:ss)"
            data-action="edit-marker-time" data-song-id="${song.id}" data-marker-id="${m.id}" />
          <input type="text" class="marker-label-input" placeholder="Section name…" value="${escapeHtml(m.label)}"
            data-action="edit-marker-label" data-song-id="${song.id}" data-marker-id="${m.id}" />
          <input type="text" class="marker-short-input" placeholder="Player label" aria-label="Player label"
            maxlength="4" value="${escapeHtml(m.shortLabel)}"
            title="Compact label shown on the Player section strip (for example IN1, V1, or BR). Leave blank to auto-generate one from the section name."
            data-action="edit-marker-short" data-song-id="${song.id}" data-marker-id="${m.id}" />
          <button class="btn btn-small btn-icon-only${markerHasAnyNotes(m.notes) || Object.values(m.noteSheets).some(sheetHasContent) ? ' has-notes' : ''}" title="Lyrics/Chords/Notes"
            data-action="toggle-marker-notes" data-song-id="${song.id}" data-marker-id="${m.id}">
            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 4h14v13l-4 4H5z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M8 9h8M8 13h5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
          <button class="btn btn-small btn-icon-only" title="Jump playhead here" data-action="jump-marker" data-song-id="${song.id}" data-marker-id="${m.id}">
            <svg viewBox="0 0 24 24" width="18" height="18"><polygon points="6,3 21,12 6,21" fill="currentColor"/></svg>
          </button>
          <button class="btn btn-small btn-icon-only" title="Delete marker" data-action="delete-marker" data-song-id="${song.id}" data-marker-id="${m.id}">
            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
          </button>
        </div>
        ${renderMarkerNotesPanel(song, m)}
      </div>`,
      )
      .join('');
  }

  /** Renders (or clears) the "Edit Markers" modal into its own root, kept
   * separate from #song-list so re-rendering the song cards never disturbs
   * it — same reasoning as why the marker editor's own state lives outside
   * renderSongs(). Re-run every time renderSongs() runs (add/edit/delete of
   * a marker, or any other store change) so the grid never shows stale data
   * while open — deleting the song it's showing also closes it, since
   * there's nothing left to edit. */
  function renderEditMarkersModal(): void {
    const song = editMarkersModalSongId ? appStore.getState().songs.find((s) => s.id === editMarkersModalSongId) : null;
    if (!song) {
      editMarkersModalSongId = null;
      editMarkersModalRoot.innerHTML = '';
      return;
    }
    editMarkersModalRoot.innerHTML = `
      <div class="modal-backdrop" data-action="close-edit-markers">
        <div class="modal modal-wide" data-action="noop">
          <h2>Edit Markers — ${escapeHtml(song.name)}</h2>
          <div class="marker-list">
            ${renderMarkerListRows(song)}
          </div>
          <div class="row" style="justify-content:flex-end; margin-top:16px">
            <button class="btn btn-primary" data-action="close-edit-markers">Done</button>
          </div>
        </div>
      </div>
    `;
    editMarkersModalRoot.querySelectorAll<HTMLElement>('.marker-notes-panel').forEach(applyNoteZoom);
  }

  /** Tab/pill bar for filtering the song list by band — "All" plus one pill
   * per band plus "Unassigned", rendered above the list, independent of
   * renderSongs() itself so switching songs' bands doesn't need to rebuild
   * this bar every time (it's only re-rendered when the filter selection or
   * the band list itself changes, both funneled through renderSongs()
   * anyway since there's no separate render loop for it). Hidden entirely
   * when there are no bands yet — a filter bar with only "All" on it is
   * pure clutter for someone who's never created a band. */
  function renderBandFilterBar(): void {
    const { bands, songs } = appStore.getState();
    if (bands.length === 0) {
      bandFilterBar.innerHTML = `<button class="btn btn-small" data-action="create-band">+ New Band</button>`;
      return;
    }
    const unassignedCount = songs.filter((s) => s.bandId === null).length;
    const activeBand = activeBandFilter && activeBandFilter !== UNASSIGNED_BAND_FILTER ? bands.find((b) => b.id === activeBandFilter) : undefined;
    const pills = [
      `<button class="pill pill-toggle${activeBandFilter === null ? ' is-active' : ''}" data-action="filter-band" data-band-id="">All (${songs.length})</button>`,
      ...bands.map(
        (band) =>
          `<button class="pill pill-toggle${activeBandFilter === band.id ? ' is-active' : ''}" data-action="filter-band" data-band-id="${band.id}">${escapeHtml(band.name)} (${songs.filter((s) => s.bandId === band.id).length})</button>`,
      ),
      `<button class="pill pill-toggle${activeBandFilter === UNASSIGNED_BAND_FILTER ? ' is-active' : ''}" data-action="filter-band" data-band-id="${UNASSIGNED_BAND_FILTER}">Unassigned (${unassignedCount})</button>`,
      `<button class="btn btn-small btn-icon-only" data-action="create-band" title="New Band">+</button>`,
      activeBand
        ? `<button class="btn btn-small btn-icon-only" data-action="rename-band" data-band-id="${activeBand.id}" title="Rename Band">
            <svg viewBox="0 0 24 24" width="16" height="16"><path d="M4 20h4L18.5 9.5a2.121 2.121 0 0 0-3-3L5 17v3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <button class="btn btn-small btn-icon-only btn-danger" data-action="delete-band" data-band-id="${activeBand.id}" title="Delete Band">
            <svg viewBox="0 0 24 24" width="16" height="16"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
          </button>`
        : '',
    ];
    bandFilterBar.innerHTML = pills.join('');
  }

  function renderSongs(): void {
    renderBandFilterBar();
    const allSongs = appStore.getState().songs;
    const bandFiltered =
      activeBandFilter === null
        ? allSongs
        : activeBandFilter === UNASSIGNED_BAND_FILTER
          ? allSongs.filter((s) => s.bandId === null)
          : allSongs.filter((s) => s.bandId === activeBandFilter);
    const trimmedQuery = songSearchQuery.trim().toLowerCase();
    const songs = trimmedQuery === '' ? bandFiltered : bandFiltered.filter((s) => s.name.toLowerCase().includes(trimmedQuery));
    songCount.textContent = String(songs.length);
    songList.innerHTML = '';

    if (allSongs.length === 0) {
      songList.innerHTML = '<p class="empty-state">No songs yet. Import a WAV file or add a metronome-only song to get started.</p>';
      return;
    }

    if (songs.length === 0) {
      songList.innerHTML = trimmedQuery !== '' ? '<p class="empty-state">No songs match your search.</p>' : '<p class="empty-state">No songs in this band yet.</p>';
      return;
    }

    // Duplicate-name detection stays global across ALL songs regardless of
    // the active band filter — two same-named songs in different bands are
    // just as likely to cause mix-ups (e.g. picking the wrong one when
    // building a setlist, which has no band filter of its own) as two in the
    // same band, so narrowing this to the filtered view would hide a real risk.
    const nameCounts = new Map<string, number>();
    for (const s of allSongs) {
      const key = s.name.trim().toLowerCase();
      nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    }

    for (const song of songs) {
      const row = document.createElement('div');
      const isExpanded = expandedSongId === song.id;
      row.className = `card song-editor-card ${isExpanded ? 'is-expanded' : 'is-collapsed'}`;
      row.dataset.songId = song.id;
      const isDuplicate = (nameCounts.get(song.name.trim().toLowerCase()) ?? 0) > 1;

      row.innerHTML = `
        <div class="song-editor-header">
          <div class="song-editor-identity">
            <div class="row song-editor-title-row">
              <button class="song-title-toggle" data-action="toggle-song-editor" data-song-id="${song.id}"
                aria-expanded="${isExpanded}" aria-controls="song-editor-body-${song.id}">
                <svg class="song-title-chevron" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                  <path d="m7 4 6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="song-title-stack">
                  <strong class="song-title">${escapeHtml(song.name)}</strong>
                  <span class="song-list-meta">${modeLabel(song.mode)}${song.durationSeconds ? ` · ${formatMarkerTime(song.durationSeconds)}` : ''}</span>
                  <span class="song-list-meta-mobile">
                    ${modeFormatPills(song.mode, song.fileFormat)}
                    ${song.durationSeconds ? `<span class="song-duration-mobile">${formatMarkerTime(song.durationSeconds)}</span>` : ''}
                  </span>
                </span>
              </button>
              <div class="song-title-support">
                <span class="song-title-badges">
                  ${isDuplicate ? '<span class="pill pill-warn song-badge-desktop">DUPLICATE NAME</span>' : ''}
                  <span class="${statusPillClass(song.status)} song-badge-desktop">${STATUS_LABEL[song.status]}</span>
                  ${isDuplicate ? '<span class="song-status-icon song-status-icon-warn song-badge-mobile" role="img" aria-label="Duplicate name"><svg viewBox="0 0 20 20" width="18" height="18"><title>Duplicate name</title><path d="M10 2.5 18 17H2Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M10 8v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="10" cy="14.5" r="1" fill="currentColor"/></svg></span>' : ''}
                  <span class="song-badge-mobile">${statusIcon(song.status)}</span>
                </span>
                ${isExpanded && song.mode !== 'metronome' ? renderInfoToggle(`file-info:${song.id}`, 'File details') : ''}
              </div>
            </div>
          </div>
          <div class="song-title-actions" aria-label="Song settings and actions" ${isExpanded ? '' : 'hidden'}>
            <label class="song-header-field">
              <span>Playback</span>
              <select class="mode-select" data-song-id="${song.id}" title="Mode">
                <option value="tracks-click" ${song.mode === 'tracks-click' ? 'selected' : ''}>Tracks + Click</option>
                <option value="tracks-only" ${song.mode === 'tracks-only' ? 'selected' : ''}>Tracks Only</option>
                <option value="metronome" ${song.mode === 'metronome' ? 'selected' : ''}>Metronome Only</option>
              </select>
            </label>
            ${
              appStore.getState().bands.length > 0
                ? `<label class="song-header-field">
                    <span>Band</span>
                    <select class="mode-select band-select" data-action="set-song-band" data-song-id="${song.id}" title="Band">
                      <option value="" ${song.bandId === null ? 'selected' : ''}>Unassigned</option>
                      ${appStore
                        .getState()
                        .bands.map((band) => `<option value="${band.id}" ${song.bandId === band.id ? 'selected' : ''}>${escapeHtml(band.name)}</option>`)
                        .join('')}
                    </select>
                  </label>`
                : ''
            }
            <div class="song-icon-actions">
              <button class="btn btn-small btn-icon-only" data-action="rename" data-song-id="${song.id}" title="Rename">
                <svg viewBox="0 0 24 24" width="16" height="16"><path d="M4 20h4L18.5 9.5a2.121 2.121 0 0 0-3-3L5 17v3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              ${
                song.mode !== 'metronome'
                  ? `<button class="btn btn-small btn-icon-only" data-action="replace" data-song-id="${song.id}" title="Replace File">
                      <svg viewBox="0 0 24 24" width="16" height="16"><path d="M4 4v6h6M20 20v-6h-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.6 15A8 8 0 0 0 20 12M19.4 9A8 8 0 0 0 4 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                    </button>`
                  : ''
              }
              <button class="btn btn-small btn-icon-only btn-danger" data-action="delete" data-song-id="${song.id}" title="Delete">
                <svg viewBox="0 0 24 24" width="16" height="16"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
              </button>
              ${
                supabaseConfigured && authStore.isSignedIn()
                  ? `<button class="btn btn-small btn-icon-only" data-action="sync-to-cloud-band" data-song-id="${song.id}" title="Sync to Cloud Band">
                      <svg viewBox="0 0 24 24" width="16" height="16"><path d="M7 18a4.5 4.5 0 0 1-.4-8.98A5.5 5.5 0 0 1 17.3 8.02 4 4 0 0 1 17 16H7z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </button>`
                  : ''
              }
            </div>
          </div>
        </div>
        <div class="song-editor-body" id="song-editor-body-${song.id}" ${isExpanded ? '' : 'hidden'}>
        ${
          // For file-based songs, mode is already shown in the Mode select
          // and duration is already shown in the mini-player below — this
          // line would be pure redundancy. Metronome-only songs have
          // neither of those (no file, no mini-player), so they still need
          // a summary line for their BPM/time signature.
          song.mode === 'metronome'
            ? `<p class="text-dim song-subline">${modeLabel(song.mode)} · ${song.metronomeSettings.bpm} BPM, ${song.metronomeSettings.beatsPerMeasure}/${song.metronomeSettings.beatUnit}</p>`
            : ''
        }
        ${
          song.mode !== 'metronome'
            ? renderInfoPanel(
                `file-info:${song.id}`,
                `<p class="text-dim" style="margin:0">
                  ${(song.fileFormat ?? '?').toUpperCase()} ·
                  ${song.fileFormat === 'mp3' ? 'channels not verified' : `${song.channelCount ?? '?'} channel(s)`} ·
                  ${formatBytes(song.fileSizeBytes)}
                  ${song.originalFileName ? `<br>Original file: ${escapeHtml(song.originalFileName)}` : ''}
                </p>`,
              )
            : ''
        }
        ${
          // Every remaining status message (missing file, unreadable, mono,
          // etc.) is a real per-song problem worth surfacing unprompted —
          // MP3 no longer carries one of its own (see inspectAudioFile).
          song.statusMessage
            ? `<p class="text-warn" style="margin:4px 0">${escapeHtml(song.statusMessage)}</p>`
            : ''
        }
        ${song.mode !== 'metronome' ? `<div class="song-edit-workspace">${renderMarkersSection(song)}</div>` : ''}
        </div>
      `;
      songList.appendChild(row);
    }
    drawMiniWaveform();
    drawBeatAlignmentStrip();
    renderEditMarkersModal();

    // Arriving here via a #/library/{songId} link (the Player's "Edit"
    // pencil, a marker/click-settings shortcut) expands that song's row (see
    // expandedSongId/libraryRouteTarget above), but a long library list can
    // easily place that row well below the fold — the row opened correctly
    // with nothing bringing it into view, which from the user's side looked
    // like the edit link "went to the Library page, but not to the song
    // itself." Same pattern as setlists.ts's own setlistRouteTarget scroll.
    if (libraryRouteTarget) {
      requestAnimationFrame(() => songList.querySelector<HTMLElement>(`[data-song-id="${expandedSongId}"]`)?.scrollIntoView({ block: 'start' }));
    }
  }

  /** Draws the decoded peak overview into the mini-player's canvas, sized to
   * its actual rendered CSS size × devicePixelRatio for a crisp line at any
   * screen density. No-op until decodeWaveformPeaks resolves for whichever
   * song is currently loaded (loadMarkerAudioIfNeeded re-renders once it does). */
  function drawMiniWaveform(): void {
    if (!miniWaveformPeaks || miniWaveformSongId !== loadedMarkerSongId) return;
    // Every song's editor body owns a .mini-waveform-canvas, so an unscoped
    // querySelector could find a collapsed song before the expanded one
    // whose peaks were actually decoded —
    // scope to that specific song's own .mini-player via data-song-id.
    const canvas = songList.querySelector<HTMLCanvasElement>(
      `.mini-player[data-song-id="${loadedMarkerSongId}"] .mini-waveform-canvas`,
    );
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const accent = getComputedStyle(canvas).getPropertyValue('--waveform-color').trim() || 'rgba(255,255,255,0.35)';
    drawWaveform(canvas, miniWaveformPeaks, accent);
  }

  /** Fixed-duration window (in seconds) shown by the beat-alignment strip,
   * always centered on the current playhead — chosen to comfortably show a
   * few beats of context at typical song tempos without being so wide that
   * fine misalignment (a few tens of ms) becomes hard to see by eye. */
  const BEAT_ALIGNMENT_WINDOW_SECONDS = 2;

  /** The exact center time the zoomed strip is currently drawn around for
   * `song` — shared by drawBeatAlignmentStrip (to know what to draw) and the
   * strip's own tap/drag handler (to know what a touched pixel corresponds
   * to), so the two can never drift apart into "the view says one thing but
   * a tap computes against another." With a synthetic click configured,
   * centers on the click beat NEAREST the playhead (not the raw playhead
   * time) — so that nudging the offset (which shifts where that nearest
   * beat actually falls) pans the waveform slice by the same amount as it
   * shifts the ticks, and both layers move together as one strip. Without a
   * click (no BPM to compute a beat grid from at all), centers on the
   * playhead directly — the strip is still useful purely as a zoomed-in
   * waveform view for marker placement, it just has no ticks to align to. */
  function beatAlignmentCenterTime(song: SongRecord): number {
    const effectiveTime = miniVirtualBeatTime ?? miniCurrentTime;
    return song.syntheticClickEnabled
      ? nearestBeatTime(effectiveTime, song.metronomeSettings.bpm, song.syntheticClickOffsetSeconds)
      : effectiveTime;
  }

  function beatAlignmentSelectedTime(): number {
    return miniVirtualBeatTime ?? markerEditorPlayer.currentTime ?? miniCurrentTime;
  }

  /** Draws the zoomed, playhead-centered waveform slice + beat ticks for
   * whichever song's synthetic-click panel is open. A no-op (and the strip
   * stays visually blank) until the full-song decode resolves, same as
   * drawMiniWaveform — this reuses that same decoded peak data rather than
   * decoding again, just windowed differently. */
  function drawBeatAlignmentStrip(): void {
    if (!miniWaveformPeaks || miniWaveformSongId !== loadedMarkerSongId) return;
    // Same reasoning as drawMiniWaveform above — every song's card has its
    // own .beat-alignment-canvas now, so this must be scoped to the
    // currently loaded song specifically, not just the first one in the list.
    const canvas = songList.querySelector<HTMLCanvasElement>(
      `.beat-alignment-strip[data-song-id="${loadedMarkerSongId}"] .beat-alignment-canvas`,
    );
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const song = appStore.getState().songs.find((s) => s.id === loadedMarkerSongId);
    if (!song) return;

    const centerTime = beatAlignmentCenterTime(song);
    const startSeconds = centerTime - BEAT_ALIGNMENT_WINDOW_SECONDS / 2;
    const style = getComputedStyle(canvas);
    const waveformColor = style.getPropertyValue('--waveform-color').trim() || 'rgba(255,255,255,0.35)';
    drawWaveformWindow(canvas, miniWaveformPeaks, startSeconds, BEAT_ALIGNMENT_WINDOW_SECONDS, waveformColor);

    const ticks = song.syntheticClickEnabled
      ? computeBeatTicks(
          startSeconds,
          BEAT_ALIGNMENT_WINDOW_SECONDS,
          song.metronomeSettings.bpm,
          song.syntheticClickOffsetSeconds,
          song.beatOneReferenceSeconds,
        )
      : [];
    // drawBeatTicks always draws the fixed center playhead line (with its
    // caret arrows) even with an empty tick array, so this single call
    // covers both the "no click configured yet" and "click configured"
    // cases — no separate playhead-only fallback path needed.
    drawBeatTicks(canvas, ticks, startSeconds, BEAT_ALIGNMENT_WINDOW_SECONDS, {
      beat: style.getPropertyValue('--beat-tick-color').trim() || 'rgba(255,255,255,0.5)',
      playhead: style.getPropertyValue('--playhead-color').trim() || '#ff5555',
    });

    renderStartOfFileLabel(song, startSeconds);
    renderFirstBeatLabel(song, startSeconds);
    renderBeatAlignmentMarkerLabels(song, startSeconds);
  }

  /** When the zoomed strip extends before 0:00, shows a visible "file
   * start" guide exactly at absolute time 0 so the user has an explicit
   * earliest-possible target for scrubbing/marking near the song's intro,
   * instead of guessing where the clamped media boundary begins inside the
   * negative-time padding. */
  function renderStartOfFileLabel(song: SongRecord, startSeconds: number): void {
    const container = songList.querySelector<HTMLElement>(
      `.beat-alignment-strip[data-song-id="${song.id}"] .beat-alignment-start-label`,
    );
    if (!container) return;
    const endSeconds = startSeconds + BEAT_ALIGNMENT_WINDOW_SECONDS;
    if (startSeconds >= 0 || endSeconds <= 0) {
      container.innerHTML = '';
      return;
    }
    const leftPercent = ((0 - startSeconds) / BEAT_ALIGNMENT_WINDOW_SECONDS) * 100;
    container.innerHTML = `
      <span class="beat-alignment-start-tag" style="left:${leftPercent}%">
        <span class="beat-alignment-start-line" aria-hidden="true"></span>
        <span class="beat-alignment-start-chip">Start of file</span>
      </span>
    `;
  }

  /** Positions a small "1st Beat" label at the song's marked first-beat
   * position, if it falls within the beat-alignment strip's current
   * window — an HTML overlay (not drawn on the canvas), same pattern as
   * the marker labels below, so the marked reference point is visually
   * named just like a real marker would be, not just a plain tick. */
  function renderFirstBeatLabel(song: SongRecord, startSeconds: number): void {
    // Scoped to this specific song's strip — every song's card has its own
    // .beat-alignment-first-beat-label now, not just one "open" song's.
    const container = songList.querySelector<HTMLElement>(
      `.beat-alignment-strip[data-song-id="${song.id}"] .beat-alignment-first-beat-label`,
    );
    if (!container) return;
    const firstBeatTimeSeconds = song.firstBeatTimeSeconds;
    const endSeconds = startSeconds + BEAT_ALIGNMENT_WINDOW_SECONDS;
    if (firstBeatTimeSeconds === null || firstBeatTimeSeconds < startSeconds || firstBeatTimeSeconds > endSeconds) {
      container.innerHTML = '';
      return;
    }
    const leftPercent = ((firstBeatTimeSeconds - startSeconds) / BEAT_ALIGNMENT_WINDOW_SECONDS) * 100;
    container.innerHTML = `<span class="beat-alignment-first-beat-tag" style="left:${leftPercent}%">1st Beat</span>`;
  }

  /** Positions a small label for each of the song's markers that falls
   * within the beat-alignment strip's current ~2s window — an HTML overlay
   * (not drawn on the canvas) so it gets normal text truncation/readability,
   * positioned with the same left% math as the canvas ticks. Only markers
   * actually visible in the window are rendered, recomputed on every redraw
   * since the window pans as the playhead moves or the offset is nudged.
   *
   * Always a plain static span, never a live <input> here — this strip
   * redraws on every playhead timeupdate AND on every window resize (see
   * handleResize below), and on Android, focusing a text input opens the
   * on-screen keyboard, which itself fires viewport resize events. That
   * combination used to rebuild+refocus a BRAND NEW <input> node on this
   * same 96px canvas-holding strip on every one of those resize ticks
   * during the keyboard's open animation — several times in quick
   * succession, each one potentially re-triggering another resize — which
   * is what caused the flicker/crash loop reported on Android when adding
   * a marker. Naming a freshly-placed marker now always happens via the
   * marker list's own .marker-label-input below (a plain in-flow input,
   * never rebuilt by this strip's redraws) — see the add-marker-here
   * handler, which focuses that row directly instead of anything here. */
  function renderBeatAlignmentMarkerLabels(song: SongRecord, startSeconds: number): void {
    // Scoped to this specific song's strip — same reasoning as
    // renderFirstBeatLabel above.
    const container = songList.querySelector<HTMLElement>(
      `.beat-alignment-strip[data-song-id="${song.id}"] .beat-alignment-marker-labels`,
    );
    if (!container) return;
    const endSeconds = startSeconds + BEAT_ALIGNMENT_WINDOW_SECONDS;
    const visible = song.markers.filter((m) => m.timeSeconds >= startSeconds && m.timeSeconds <= endSeconds);

    container.innerHTML = visible
      .map((m) => {
        const leftPercent = ((m.timeSeconds - startSeconds) / BEAT_ALIGNMENT_WINDOW_SECONDS) * 100;
        return `<span class="beat-alignment-marker-label" style="left:${leftPercent}%" title="${escapeHtml(m.label || 'Untitled marker')} (${formatMarkerTime(m.timeSeconds)})">${effectiveShortLabel(m)}</span>`;
      })
      .join('');
  }

  async function handleImportFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    const supported = fileArray.filter((f) => /\.(wav|mp3)$/i.test(f.name));
    const unsupported = fileArray.filter((f) => !/\.(wav|mp3)$/i.test(f.name));
    if (unsupported.length > 0) {
      showToast(`${unsupported.length} file(s) skipped: only .wav and .mp3 files are supported.`, 'warn');
    }
    if (supported.length === 0) return;

    const { imported, duplicateNames } = await appStore.importSongs(supported);
    showToast(`Imported ${imported.length} song(s).`, 'info');
    if (duplicateNames.length > 0) {
      showToast(`Duplicate name(s) detected: ${duplicateNames.join(', ')}`, 'warn');
    }
    const monoWarnings = imported.filter((s) => s.status === 'mono-warning');
    if (monoWarnings.length > 0) {
      showToast(`${monoWarnings.length} imported file(s) are mono, not stereo.`, 'warn');
    }
  }

  songSearchInput.addEventListener('input', () => {
    songSearchQuery = songSearchInput.value;
    renderSongs();
    // renderSongs() rebuilds #song-list, not this input itself (it lives
    // outside songList's innerHTML), so its value/focus/cursor position are
    // untouched by the re-render — nothing to restore here.
  });

  importBtn.addEventListener('click', () => wavInput.click());
  wavInput.addEventListener('change', () => {
    void handleImportFiles(wavInput.files);
    wavInput.value = '';
  });

  addMetronomeBtn.addEventListener('click', async () => {
    const name = await promptDialog({ title: 'Add Metronome Song', initialValue: 'Click Track', confirmLabel: 'Add' });
    if (!name) return;
    await appStore.createMetronomeSong(name);
    showToast('Metronome song added.', 'info');
  });

  /** Tears down whichever song's audio is currently loaded, resetting all
   * mini-player/strip/sub-panel state — called before loading a DIFFERENT
   * song's audio (only one plays at a time) or when that song is deleted. */
  function unloadMarkerAudio(): void {
    if (loadedMarkerSongId === null) return;
    loadedMarkerSongId = null;
    markerEditorPlayer.teardown();
    isMiniPlaying = false;
    miniCurrentTime = 0;
    miniDuration = 0;
    miniVirtualBeatTime = null;
    miniWaveformPeaks = null;
    miniWaveformSongId = null;
    markerSubPanel = null;
  }

  /** Loads (or reloads) `song`'s audio into the marker editor's mini-player,
   * assuming `loadedMarkerSongId` is already set to it (by the caller, before
   * this runs, so the UI can optimistically show "this song is now active"
   * immediately rather than waiting on the audio to actually resolve). Split
   * out from loadMarkerAudioIfNeeded so the synthetic-click-route-left-only
   * toggle can force a reload (that routing is wired once at load time, not
   * something syncSyntheticClick can patch live) without duplicating the
   * "is this already the loaded song" check. */
  async function loadMarkerEditorAudio(song: SongRecord): Promise<void> {
    const audioRecord = await database.getAudioBlob(song.id);
    if (!audioRecord) {
      showToast('No audio file is stored for this song. Re-import it to add markers.', 'warn');
      return;
    }
    try {
      // Routing is a setlist-level setting now (see SetlistRouting) — the
      // Library's mini-player isn't playing as part of any setlist, so it
      // always previews in plain stereo rather than guessing which
      // setlist/routing this song might eventually be played under.
      await markerEditorPlayer.loadIfNeeded(song.id, audioRecord.blob, 'stereo');
      miniDuration = markerEditorPlayer.duration;
      syncSyntheticClick(song);
      renderSongs();
    } catch {
      showToast('Could not load this song\'s audio for marker editing.', 'warn');
      return;
    }

    // Decoded separately from (and after) loadIfNeeded's own streaming
    // <audio> setup above — this is a one-shot full decode purely to get a
    // peak overview for drawing, not part of the actual playback graph.
    // Runs in the background; if the user has since closed/switched editors
    // by the time it resolves, the result is simply discarded.
    try {
      const peaks = await decodeWaveformPeaks(audioRecord.blob);
      if (loadedMarkerSongId !== song.id) return;
      miniWaveformPeaks = peaks;
      miniWaveformSongId = song.id;
      renderSongs();
    } catch {
      // No waveform is a cosmetic loss, not a functional one — the mini
      // player still works fine without it (e.g. an audio format the
      // decodeAudioData path can't handle even though playback via
      // <audio src> can).
    }
  }

  /** Loads `song`'s audio into the shared mini-player if it isn't already
   * the active one — called at the top of every marker-editor action
   * (Play, restart, scrub, nudge, sub-panel toggle, Set Marker Here, ...)
   * after that song's compact row has been expanded. Opening the row itself
   * stays lightweight; the first playback/edit interaction loads audio. A
   * no-op if this song is
   * already loaded, so repeated calls (e.g. clicking Play right after a
   * nudge) don't reload audio that's already there. */
  async function loadMarkerAudioIfNeeded(song: SongRecord): Promise<void> {
    if (loadedMarkerSongId === song.id) return;
    unloadMarkerAudio();
    loadedMarkerSongId = song.id;
    renderSongs();
    await loadMarkerEditorAudio(song);
  }

  /** Keeps the marker editor player's synthetic click config in step with
   * whatever the currently-open song's own record says — called after
   * loading a song's audio and after any edit to its BPM/offset/toggle, so
   * the tuning tool never plays against stale settings. */
  function syncSyntheticClick(song: SongRecord): void {
    if (markerEditorPlayer.currentSongId !== song.id) return;
    markerEditorPlayer.setSyntheticClick({
      enabled: song.syntheticClickEnabled,
      settings: song.metronomeSettings,
      offsetSeconds: song.syntheticClickOffsetSeconds,
      // Fixed preview side (not read from the song — routing is a
      // setlist-level setting now, see SetlistRouting) — 'right' keeps the
      // click audibly separated from the track's own audio while tuning by
      // ear, matching this feature's original default before routing had
      // any per-song or per-setlist concept at all.
      side: 'right',
    });
  }

  markerEditorPlayer.subscribe((event: MarkerPlayerEvent) => {
    if (loadedMarkerSongId !== markerEditorPlayer.currentSongId) return;
    miniVirtualBeatTime = null;
    miniCurrentTime = event.currentTime;
    miniDuration = event.duration || miniDuration;
    isMiniPlaying = event.type === 'playing';

    // 'timeupdate' fires many times per second during playback. A full
    // renderSongs() on every tick would destroy/rebuild the marker time and
    // label <input> elements constantly, kicking focus out of whichever one
    // the user is mid-edit in. Patch just the mini-player's own bits instead;
    // a full re-render still happens for play/pause/ended (rare) and for any
    // store-driven change (add/edit/delete marker).
    if (event.type === 'timeupdate') {
      patchMiniPlayerProgress();
    } else {
      renderSongs();
    }
  });

  function patchMiniPlayerProgress(): void {
    // Scoped to the currently loaded, expanded song's mini-player so an
    // unscoped querySelector can never patch a collapsed row's controls.
    const scope = songList.querySelector<HTMLElement>(`.mini-player[data-song-id="${loadedMarkerSongId}"]`);
    const fill = scope?.querySelector<HTMLElement>('.mini-scrub-fill');
    const time = scope?.querySelector<HTMLElement>('.mini-time');
    if (fill) fill.style.width = `${miniDuration > 0 ? (miniCurrentTime / miniDuration) * 100 : 0}%`;
    if (time) time.textContent = `${formatMarkerTime(miniCurrentTime)} / ${formatMarkerTime(miniDuration)}`;
    // The alignment strip is centered on the playhead, so it must redraw on
    // every tick (unlike the full-song waveform, which never moves) — still
    // a targeted canvas redraw, not a renderSongs(), so no input focus is at risk.
    drawBeatAlignmentStrip();
  }

  /** Shared delegated click handler for both #song-list and the "Edit
   * Markers" modal — the modal renders into its own root (see
   * renderEditMarkersModal's doc comment for why), but its jump/delete
   * marker buttons carry the exact same data-action/data-song-id/
   * data-marker-id attributes as the inline grid used to, so one dispatch
   * function serves both rather than maintaining two parallel copies of
   * this same if/else-if chain. */
  async function handleActionClick(e: MouseEvent): Promise<void> {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    const action = target?.dataset.action;
    if (!action) return;

    if (action === 'close-edit-markers') {
      // Fires from a click directly on the backdrop (dismiss by clicking
      // outside the card) or the "Done" button — both carry this same
      // data-action, and nothing else inside the modal card does, so
      // .closest() only ever matches one of those two elements here.
      editMarkersModalSongId = null;
      renderEditMarkersModal();
      return;
    }

    if (action === 'toggle-info') {
      const key = target?.dataset.infoKey;
      if (!key) return;
      if (expandedInfoKeys.has(key)) expandedInfoKeys.delete(key);
      else expandedInfoKeys.add(key);
      renderSongs();
      return;
    }

    if (action === 'toggle-song-editor') {
      const songId = target.dataset.songId;
      if (!songId) return;
      const isClosing = expandedSongId === songId;
      if (loadedMarkerSongId !== null && (isClosing || loadedMarkerSongId !== songId)) unloadMarkerAudio();
      expandedSongId = isClosing ? null : songId;
      markerSubPanel = null;
      renderSongs();
      // Expanding a row is already "I want to edit this song" — loading its
      // audio right away (rather than waiting for the first scrub/play
      // click, see loadMarkerAudioIfNeeded's own doc comment for the prior
      // lazy-load reasoning) means the waveform/mini-player is ready
      // immediately instead of showing a "Click to Edit" placeholder that
      // read as a leftover extra step. Metronome Only songs have no audio
      // file at all, so skip the load entirely rather than firing
      // loadMarkerEditorAudio's own "No audio file is stored" toast on
      // every single expand of a click-only song.
      if (!isClosing) {
        const song = appStore.getState().songs.find((s) => s.id === songId);
        if (song && song.mode !== 'metronome') void loadMarkerAudioIfNeeded(song);
      }
      return;
    }

    if (action === 'filter-band') {
      const bandId = target?.dataset.bandId ?? '';
      activeBandFilter = bandId === '' ? null : bandId;
      renderSongs();
      return;
    }

    if (action === 'create-band') {
      const name = await promptDialog({ title: 'New Band', placeholder: 'e.g. The Moonlighters', confirmLabel: 'Create' });
      if (name) {
        const band = await appStore.createBand(name);
        activeBandFilter = band.id;
        renderSongs();
      }
      return;
    }

    if (action === 'rename-band') {
      const bandId = target?.dataset.bandId;
      const band = bandId ? appStore.getState().bands.find((b) => b.id === bandId) : undefined;
      if (!band) return;
      const newName = await promptDialog({ title: 'Rename Band', initialValue: band.name, confirmLabel: 'Rename' });
      if (newName) {
        await appStore.renameBand(band.id, newName);
        renderSongs();
      }
      return;
    }

    if (action === 'delete-band') {
      const bandId = target?.dataset.bandId;
      const band = bandId ? appStore.getState().bands.find((b) => b.id === bandId) : undefined;
      if (!band) return;
      const confirmed = await confirmDialog({
        title: 'Delete Band',
        message: `Delete "${band.name}"? Its songs are NOT deleted — they move to Unassigned. This cannot be undone.`,
        confirmLabel: 'Delete',
        danger: true,
      });
      if (confirmed) {
        await appStore.deleteBand(band.id);
        activeBandFilter = null;
        renderSongs();
        showToast(`Deleted "${band.name}". Its songs moved to Unassigned.`, 'info');
      }
      return;
    }

    const songId = target?.dataset.songId;
    if (!songId) return;
    const song = appStore.getState().songs.find((s) => s.id === songId);
    if (!song) return;

    if (action === 'rename') {
      const newName = await promptDialog({ title: 'Rename Song', initialValue: song.name, confirmLabel: 'Rename' });
      if (newName) {
        await appStore.renameSong(songId, newName);
      }
    } else if (action === 'replace') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'audio/wav,audio/x-wav,.wav,audio/mpeg,.mp3';
      input.addEventListener('change', async () => {
        const file = input.files?.[0];
        if (!file) return;
        const { clickWasCleared } = await appStore.replaceSongFile(songId, file);
        if (clickWasCleared) {
          showToast(
            `Replaced audio for "${song.name}". Its custom click was turned off — browsers cannot seek MP3 precisely enough to keep a click in sync.`,
            'warn',
          );
        } else {
          showToast(`Replaced audio for "${song.name}".`, 'info');
        }
      });
      input.click();
    } else if (action === 'delete') {
      const confirmed = await confirmDialog({
        title: 'Delete Song',
        message: `Delete "${song.name}" from the library? This also removes it from any setlists. This cannot be undone.`,
        confirmLabel: 'Delete',
        danger: true,
      });
      if (confirmed) {
        if (loadedMarkerSongId === songId) unloadMarkerAudio();
        await appStore.deleteSong(songId);
        showToast(`Deleted "${song.name}".`, 'info');
      }
    } else if (action === 'sync-to-cloud-band') {
      if (myCloudBandsCache === null) {
        const result = await listMyBands();
        if (result.error) {
          showToast(result.error, 'warn');
          return;
        }
        myCloudBandsCache = result.bands;
      }
      if (myCloudBandsCache.length === 0) {
        showToast('You need to create a Cloud Band first — go to Settings → Account.', 'warn');
        return;
      }
      let targetBandId: string;
      if (myCloudBandsCache.length === 1) {
        const only = myCloudBandsCache[0];
        const confirmed = await confirmDialog({
          title: 'Sync to Cloud Band',
          message: `Push "${song.name}" (and its markers) to "${only.name}"? Re-syncing later updates the same cloud copy rather than creating a duplicate.`,
          confirmLabel: 'Sync',
        });
        if (!confirmed) return;
        targetBandId = only.id;
      } else {
        const selected = await selectDialog({
          title: 'Sync to Cloud Band',
          message: `Push "${song.name}" (and its markers) to which Cloud Band?`,
          options: myCloudBandsCache.map((b) => ({ value: b.id, label: b.name })),
          confirmLabel: 'Sync',
        });
        if (!selected) return;
        targetBandId = selected;
      }
      const { supabaseSongId, error } = await syncSongToBand(song, targetBandId);
      if (error) {
        showToast(error, 'warn');
        return;
      }
      if (supabaseSongId && supabaseSongId !== song.supabaseSongId) {
        await appStore.updateSong(songId, { supabaseSongId });
      }
      showToast(`Synced "${song.name}" to the cloud.`, 'info');
    } else if (action === 'open-edit-markers') {
      editMarkersModalSongId = songId;
      renderEditMarkersModal();
    } else if (action === 'toggle-add-markers') {
      await loadMarkerAudioIfNeeded(song);
      markerSubPanel = markerSubPanel === 'markers' ? null : 'markers';
      renderSongs();
    } else if (action === 'toggle-add-click') {
      // Guards the same condition the button's own `disabled` attribute
      // already covers (see renderMarkersSection) — belt-and-suspenders
      // against this ever firing some other way, not the primary gate.
      if (song.fileFormat === 'mp3') return;
      await loadMarkerAudioIfNeeded(song);
      markerSubPanel = markerSubPanel === 'click' ? null : 'click';
      renderSongs();
    } else if (action === 'toggle-count-in') {
      await loadMarkerAudioIfNeeded(song);
      markerSubPanel = markerSubPanel === 'countin' ? null : 'countin';
      renderSongs();
    } else if (action === 'set-count-in-beats') {
      const beats = Number(target.dataset.beats);
      // Beat-count options only ever render when this song has a synthetic
      // click enabled (see renderCountInSection), so its BPM is always
      // available here.
      const seconds = beats * secondsPerBeat(song.metronomeSettings);
      await appStore.setSongMarkerLeadIn(songId, seconds, beats);
      renderSongs();
    } else if (action === 'set-count-in-seconds') {
      await appStore.setSongMarkerLeadIn(songId, Number(target.dataset.seconds), null);
      renderSongs();
    } else if (action === 'mini-play-pause') {
      await loadMarkerAudioIfNeeded(song);
      if (markerEditorPlayer.currentSongId !== songId) return;
      if (markerEditorPlayer.isPaused) await markerEditorPlayer.play();
      else markerEditorPlayer.pause();
    } else if (action === 'mini-restart') {
      await loadMarkerAudioIfNeeded(song);
      if (markerEditorPlayer.currentSongId !== songId) return;
      markerEditorPlayer.seek(0);
    } else if (action === 'mini-scrub') {
      await loadMarkerAudioIfNeeded(song);
      if (markerEditorPlayer.currentSongId !== songId) return;
      const track = target as HTMLElement;
      const rect = track.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      markerEditorPlayer.seek(pct * markerEditorPlayer.duration);
    } else if (action === 'add-marker-here') {
      await loadMarkerAudioIfNeeded(song);
      if (markerEditorPlayer.currentSongId !== songId) return;
      const marker = await appStore.addMarker(songId, markerEditorPlayer.currentTime, '');
      renderSongs();
      if (marker) {
        // Naming a freshly-placed marker always happens via the marker
        // list's own row below, not an overlay on the waveform strip — see
        // renderBeatAlignmentMarkerLabels's own comment for why an on-strip
        // input caused an Android keyboard-open resize/flicker loop.
        const input = songList.querySelector<HTMLInputElement>(`.marker-row[data-marker-id="${marker.id}"] .marker-label-input`);
        input?.focus();
      }
    } else if (action === 'jump-marker') {
      await loadMarkerAudioIfNeeded(song);
      if (markerEditorPlayer.currentSongId !== songId) return;
      const markerId = target?.dataset.markerId;
      const marker = song.markers.find((m) => m.id === markerId);
      if (marker) markerEditorPlayer.seek(marker.timeSeconds);
    } else if (action === 'jump-first-beat') {
      await loadMarkerAudioIfNeeded(song);
      if (markerEditorPlayer.currentSongId !== songId) return;
      if (song.firstBeatTimeSeconds !== null) markerEditorPlayer.seek(song.firstBeatTimeSeconds);
    } else if (action === 'delete-marker') {
      const markerId = target?.dataset.markerId;
      if (markerId) await appStore.deleteMarker(songId, markerId);
    } else if (action === 'toggle-marker-notes') {
      const markerId = target?.dataset.markerId;
      if (!markerId) return;
      if (expandedMarkerNotes.has(markerId)) expandedMarkerNotes.delete(markerId);
      else expandedMarkerNotes.add(markerId);
      // renderSongs(), NOT renderEditMarkersModal() alone — expandedMarkerNotes
      // is local-only state (no appStore call here for the subscription-driven
      // renderSongs() at the bottom of this file to pick up on its own), and
      // the marker grid a real user actually sees is .marker-list-inline
      // (rendered by renderSongs()), not the separate #edit-markers-modal-root
      // renderEditMarkersModal() targets — that modal has no current trigger
      // in the UI at all. Calling renderEditMarkersModal() alone left this
      // toggle looking like a dead button: the click registered (state did
      // change) but nothing ever appeared until some UNRELATED store update
      // (e.g. editing the marker label) incidentally called renderSongs()
      // afterward. renderSongs() itself still calls renderEditMarkersModal()
      // at its own end, so nothing is lost if that modal ever gains a real
      // trigger later.
      renderSongs();
    } else if (action === 'set-notes-tab') {
      const markerId = target?.dataset.markerId;
      const tab = target?.dataset.notesTab as MarkerNoteTab | undefined;
      if (!markerId || !tab) return;
      activeNotesTabByMarker.set(markerId, tab);
      renderSongs(); // local-only state — see toggle-marker-notes's own comment above
    } else if (action === 'add-note-tab') {
      const label = await promptDialog({ title: 'Add Note Tab', message: 'E.g. Bass, Keys, Cues.', placeholder: 'Tab name', confirmLabel: 'Add' });
      if (!label) return;
      const tab = await appStore.addCustomNoteTab(label);
      // Switch every currently-expanded marker's active tab to the new one
      // so it's immediately visible rather than requiring a second tap —
      // the tab was just created specifically to be used right now. This
      // local mutation happens AFTER the appStore call above already fired
      // its own subscription-driven render, so an explicit renderSongs()
      // here (not renderEditMarkersModal() alone) is what actually shows
      // the marker landing on its new tab, not the one active before.
      for (const markerId of expandedMarkerNotes) activeNotesTabByMarker.set(markerId, tab.id);
      renderSongs();
    } else if (action === 'rename-note-tab') {
      const tabId = target?.dataset.noteTabId;
      const existing = appStore.getState().settings.customNoteTabs.find((t) => t.id === tabId);
      if (!tabId || !existing) return;
      const label = await promptDialog({ title: 'Rename Note Tab', initialValue: existing.label, confirmLabel: 'Rename' });
      if (!label) return;
      await appStore.renameCustomNoteTab(tabId, label);
      renderSongs();
    } else if (action === 'delete-note-tab') {
      const tabId = target?.dataset.noteTabId;
      const existing = appStore.getState().settings.customNoteTabs.find((t) => t.id === tabId);
      if (!tabId || !existing) return;
      const confirmed = await confirmDialog({
        title: 'Delete Note Tab',
        message: `Remove the "${existing.label}" tab from every song? Any notes already written in it stay stored but become unreachable through this tab picker — they aren't deleted outright, but there's no way back to them from here.`,
        confirmLabel: 'Delete Tab',
        danger: true,
      });
      if (!confirmed) return;
      await appStore.removeCustomNoteTab(tabId);
      // Any marker currently showing the now-deleted tab falls back to
      // Lyrics rather than continuing to reference a tab id that no longer
      // appears anywhere in the picker — same "local mutation after the
      // appStore await" reasoning as add-note-tab above for why this needs
      // its own renderSongs() call.
      for (const [markerId, tab] of activeNotesTabByMarker) {
        if (tab === tabId) activeNotesTabByMarker.set(markerId, 'lyrics');
      }
      renderSongs();
    } else if (action === 'set-notes-format') {
      const markerId = target?.dataset.markerId;
      const tab = target?.dataset.notesTab as MarkerNoteTab | undefined;
      const format = target?.dataset.noteFormat as MarkerNoteFormat | undefined;
      const marker = song.markers.find((item) => item.id === markerId);
      if (!markerId || !tab || !format || !marker) return;
      const currentFormat = markerNoteFormat(marker, tab);
      if (format !== currentFormat) {
        const sourceHasContent = noteFormatHasContent(marker, tab, currentFormat);
        const targetHasContent = noteFormatHasContent(marker, tab, format);
        if (sourceHasContent && targetHasContent) {
          const confirmed = await confirmDialog({
            title: `Keep ${format === 'text' ? 'Text' : 'Sheet'} version?`,
            message: `This ${noteTabLabel(tab, appStore.getState().settings.customNoteTabs)} tab already has both Text and Sheet content. Switching will keep the ${format === 'text' ? 'Text' : 'Sheet'} version and remove the other one so nobody misses hidden notes.`,
            confirmLabel: `Keep ${format === 'text' ? 'Text' : 'Sheet'}`,
            danger: true,
          });
          if (!confirmed) return;
        }
      }
      await appStore.updateMarker(songId, markerId, buildSingleFormatNotePatch(marker, tab, format));
      renderSongs();
    } else if (action === 'adjust-note-zoom') {
      const format = target.dataset.noteFormat as MarkerNoteFormat | undefined;
      const direction = Number(target.dataset.zoomDirection) as -1 | 1;
      if (!format || (direction !== -1 && direction !== 1)) return;
      stepNoteZoom(format, direction);
      renderSongs(); // local-only state (see toggle-marker-notes's own comment above)
    } else if (action === 'clear-marker-sheet') {
      const markerId = target?.dataset.markerId;
      const tab = target?.dataset.notesTab as MarkerNoteTab | undefined;
      const marker = song.markers.find((item) => item.id === markerId);
      if (!markerId || !tab || !marker) return;
      const confirmed = await confirmDialog({
        title: 'Clear Sheet',
        message: `Clear the ${noteTabLabel(tab, appStore.getState().settings.customNoteTabs)} sheet for this section? This cannot be undone.`,
        confirmLabel: 'Clear Sheet',
        danger: true,
      });
      if (confirmed) await appStore.updateMarker(songId, markerId, { noteSheets: { ...marker.noteSheets, [tab]: { rows: [] } } });
    } else if (action === 'nudge-strip') {
      // The strip's flanking ms-nudge buttons do double duty depending on
      // which sub-panel is open: under "Add Custom Click" they dial in the
      // synthetic click's own offset (shifting the click grid itself),
      // otherwise they move the mini-player's playhead (for fine-placing a
      // marker, or just scrubbing around) — one shared control surface
      // rather than two separate nudge UIs for the same physical buttons.
      await loadMarkerAudioIfNeeded(song);
      const deltaMs = Number(target?.dataset.ms);
      if (markerSubPanel === 'click') {
        const newOffset = song.syntheticClickOffsetSeconds + deltaMs / 1000;
        await appStore.updateSong(songId, { syntheticClickOffsetSeconds: newOffset });
      } else {
        const nextTime = beatAlignmentSelectedTime() + deltaMs / 1000;
        if (nextTime < 0) {
          miniVirtualBeatTime = nextTime;
          drawBeatAlignmentStrip();
        } else {
          miniVirtualBeatTime = null;
          if (markerEditorPlayer.currentSongId !== songId) return;
          markerEditorPlayer.seek(nextTime);
        }
      }
    } else if (action === 'nudge-strip-beat') {
      // Always walks the mini-player's playhead one whole beat on the click
      // grid, regardless of which sub-panel is open — there's no equivalent
      // "one beat of offset" concept for Add Custom Click, so unlike the ms
      // button above this one always means the same thing (see
      // renderNudgeStack's disabled-state comment for why it's gated on
      // syntheticClickEnabled rather than switching behavior by sub-panel).
      await loadMarkerAudioIfNeeded(song);
      const direction = target?.dataset.beats === '1' ? 1 : -1;
      const nextTime = adjacentBeatTime(
        beatAlignmentSelectedTime(),
        song.metronomeSettings.bpm,
        song.syntheticClickOffsetSeconds,
        direction,
      );
      if (nextTime < 0) {
        miniVirtualBeatTime = nextTime;
        drawBeatAlignmentStrip();
      } else {
        miniVirtualBeatTime = null;
        if (markerEditorPlayer.currentSongId !== songId) return;
        markerEditorPlayer.seek(nextTime);
      }
    } else if (action === 'mark-first-beat') {
      await loadMarkerAudioIfNeeded(song);
      // Always snap to the nearest beat on the click grid before recording —
      // removes any chance of marking a slightly-off position from a rough
      // scrub, without requiring the user to nudge by exact beats themselves.
      const snapped = nearestBeatTime(beatAlignmentSelectedTime(), song.metronomeSettings.bpm, song.syntheticClickOffsetSeconds);
      await appStore.updateSong(songId, { firstBeatTimeSeconds: snapped });
    } else if (action === 'clear-first-beat') {
      await appStore.updateSong(songId, { firstBeatTimeSeconds: null });
    } else if (action === 'mark-as-aligned') {
      await loadMarkerAudioIfNeeded(song);
      // Whatever beat the playhead is currently on becomes "beat 1" for the
      // zoomed strip's numbering — not necessarily the song's true first
      // beat (that's Mark as First Beat, above), just whichever beat the
      // user has lined the click up with. Snap to the nearest beat first,
      // same as Mark as First Beat, so a rough scrub still records a clean
      // position.
      const snapped = nearestBeatTime(beatAlignmentSelectedTime(), song.metronomeSettings.bpm, song.syntheticClickOffsetSeconds);
      await appStore.updateSong(songId, { beatOneReferenceSeconds: snapped });
    } else if (action === 'clear-beat-one-reference') {
      await appStore.updateSong(songId, { beatOneReferenceSeconds: null });
    }
  }

  songList.addEventListener('click', handleActionClick);
  editMarkersModalRoot.addEventListener('click', handleActionClick);
  bandFilterBar.addEventListener('click', handleActionClick);

  // Drag-to-pan directly on the zoomed strip: the waveform is drawn with the
  // playhead nailed to a fixed line at the strip's horizontal center (see
  // beatAlignmentCenterTime/drawBeatAlignmentStrip) — it's a tape-through-a-
  // gate view, not a free scrollable canvas. So "grab a peak and drag it"
  // has to work like pulling tape past a fixed playhead head: the peak under
  // your finger stays under your finger, which means the seek is a relative
  // delta from how far the pointer has moved since the last event, converted
  // from pixels to seconds using the strip's own window width — NOT an
  // absolute "what time is under clientX now" lookup. An absolute lookup is
  // wrong here because the window's center can itself shift between events
  // (nearestBeatTime snapping) and because it fights the finger: as the
  // pointer moves left, dragging the tape left means REVEALING content that
  // was to the right of center, i.e. seeking forward, so the delta is negated
  // relative to pointer movement direction.
  //
  // A plain tap (no movement) still jumps the playhead to the tapped pixel,
  // since that's an unambiguous "take me here" gesture with no waveform
  // position to preserve under the finger yet.
  let beatAlignmentDragSongId: string | null = null;
  let beatAlignmentDragLastX: number | null = null;
  let beatAlignmentDragMoved = false;

  songList.addEventListener('pointerdown', (e) => {
    const strip = (e.target as HTMLElement).closest<HTMLElement>('.beat-alignment-strip');
    if (!strip) return;
    const songId = strip.dataset.songId;
    if (!songId || markerEditorPlayer.currentSongId !== songId) return;
    const song = appStore.getState().songs.find((s) => s.id === songId);
    if (!song) return;

    beatAlignmentDragSongId = songId;
    beatAlignmentDragLastX = e.clientX;
    beatAlignmentDragMoved = false;
    strip.setPointerCapture(e.pointerId);
  });

  songList.addEventListener('pointermove', (e) => {
    if (!beatAlignmentDragSongId || beatAlignmentDragLastX === null) return;
    const strip = songList.querySelector<HTMLElement>(`.beat-alignment-strip[data-song-id="${beatAlignmentDragSongId}"]`);
    if (!strip) return;
    const song = appStore.getState().songs.find((s) => s.id === beatAlignmentDragSongId);
    if (!song) return;

    const deltaX = e.clientX - beatAlignmentDragLastX;
    if (deltaX === 0) return;
    beatAlignmentDragMoved = true;
    beatAlignmentDragLastX = e.clientX;

    const rect = strip.getBoundingClientRect();
    const secondsPerPixel = BEAT_ALIGNMENT_WINDOW_SECONDS / rect.width;
    const nextTime = beatAlignmentSelectedTime() - deltaX * secondsPerPixel;
    if (nextTime < 0) {
      miniVirtualBeatTime = nextTime;
      drawBeatAlignmentStrip();
    } else {
      miniVirtualBeatTime = null;
      markerEditorPlayer.seek(nextTime);
    }
  });

  const endBeatAlignmentDrag = (e: PointerEvent): void => {
    if (beatAlignmentDragSongId && !beatAlignmentDragMoved) {
      const strip = songList.querySelector<HTMLElement>(`.beat-alignment-strip[data-song-id="${beatAlignmentDragSongId}"]`);
      const song = appStore.getState().songs.find((s) => s.id === beatAlignmentDragSongId);
      if (strip && song) {
        const rect = strip.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const centerTime = beatAlignmentCenterTime(song);
        const startSeconds = centerTime - BEAT_ALIGNMENT_WINDOW_SECONDS / 2;
        const targetTime = startSeconds + pct * BEAT_ALIGNMENT_WINDOW_SECONDS;
        if (targetTime < 0) {
          miniVirtualBeatTime = targetTime;
          drawBeatAlignmentStrip();
        } else {
          miniVirtualBeatTime = null;
          markerEditorPlayer.seek(targetTime);
        }
      }
    }
    beatAlignmentDragSongId = null;
    beatAlignmentDragLastX = null;
    beatAlignmentDragMoved = false;
  };
  songList.addEventListener('pointerup', endBeatAlignmentDrag);
  songList.addEventListener('pointercancel', endBeatAlignmentDrag);

  /** Shared with the "Edit Markers" modal, same reasoning as
   * handleActionClick above — its time/label/short-code inputs carry the
   * same classes the inline grid used to. */
  async function handleFieldChange(e: Event): Promise<void> {
    const target = e.target as HTMLElement;
    if (target.classList.contains('band-select')) {
      const songId = target.dataset.songId!;
      const bandId = (target as HTMLSelectElement).value || null;
      await appStore.updateSong(songId, { bandId });
    } else if (target.classList.contains('mode-select')) {
      const songId = target.dataset.songId!;
      const mode = (target as HTMLSelectElement).value as PlaybackMode;
      await appStore.updateSong(songId, { mode });
    } else if (target.classList.contains('marker-time-input')) {
      const songId = target.dataset.songId!;
      const markerId = target.dataset.markerId!;
      const parsed = parseMarkerTime((target as HTMLInputElement).value);
      if (parsed === null) {
        showToast('Enter a time as m:ss, e.g. 1:48.', 'warn');
        renderSongs();
        return;
      }
      await appStore.updateMarker(songId, markerId, { timeSeconds: parsed });
    } else if (target.classList.contains('marker-label-input')) {
      // Commit on blur/change, not on every keystroke: the label is
      // freeform text, and re-rendering on 'input' would destroy the very
      // element the user is mid-keystroke in (losing focus/cursor position).
      const songId = target.dataset.songId!;
      const markerId = target.dataset.markerId!;
      await appStore.updateMarker(songId, markerId, { label: (target as HTMLInputElement).value });
    } else if (target.classList.contains('marker-short-input')) {
      const songId = target.dataset.songId!;
      const markerId = target.dataset.markerId!;
      await appStore.updateMarker(songId, markerId, { shortLabel: (target as HTMLInputElement).value });
    } else if (target.classList.contains('marker-notes-textarea')) {
      // Commit on blur/change, same reasoning as marker-label-input above.
      const songId = target.dataset.songId!;
      const markerId = target.dataset.markerId!;
      const tab = target.dataset.notesTab as keyof MarkerNotes;
      const song = appStore.getState().songs.find((s) => s.id === songId);
      const marker = song?.markers.find((m) => m.id === markerId);
      if (!marker) return;
      await appStore.updateMarker(songId, markerId, {
        notes: { ...marker.notes, [tab]: (target as HTMLTextAreaElement).value },
        noteFormats: { ...marker.noteFormats, [tab]: 'text' },
      });
    } else if (target.classList.contains('synthetic-click-toggle')) {
      const songId = target.dataset.songId!;
      const checked = (target as HTMLInputElement).checked;
      await appStore.updateSong(songId, { syntheticClickEnabled: checked });
    } else if (target.classList.contains('synthetic-click-bpm')) {
      const songId = target.dataset.songId!;
      const song = appStore.getState().songs.find((s) => s.id === songId);
      if (!song) return;
      const bpm = Math.min(300, Math.max(30, Number((target as HTMLInputElement).value) || DEFAULT_METRONOME_SETTINGS.bpm));
      await appStore.updateSong(songId, { metronomeSettings: { ...song.metronomeSettings, bpm } });
    } else if (target.classList.contains('synthetic-click-subdivision')) {
      const songId = target.dataset.songId!;
      const song = appStore.getState().songs.find((s) => s.id === songId);
      if (!song) return;
      const subdivision = Number((target as HTMLSelectElement).value) as 1 | 2 | 4;
      await appStore.updateSong(songId, { metronomeSettings: { ...song.metronomeSettings, subdivision } });
    }
  }

  songList.addEventListener('change', handleFieldChange);
  editMarkersModalRoot.addEventListener('change', handleFieldChange);

  editMarkersModalRoot.addEventListener('paste', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('.note-sheet-editor');
    if (!target) return;
    event.preventDefault();
    const songId = target.dataset.songId!;
    const markerId = target.dataset.markerId!;
    const tab = target.dataset.notesTab as keyof MarkerNotes;
    const song = appStore.getState().songs.find((item) => item.id === songId);
    const marker = song?.markers.find((item) => item.id === markerId);
    if (!marker) return;
    const html = event.clipboardData?.getData('text/html') ?? '';
    const plain = event.clipboardData?.getData('text/plain') ?? '';
    const sheet = parseSheetClipboard(html, plain);
    if (sheetClipboardExceedsLimits(html, plain)) {
      showToast(`Sheet limited to ${MAX_NOTE_SHEET_ROWS} rows × ${MAX_NOTE_SHEET_COLUMNS} columns.`, 'warn');
    }
    void appStore.updateMarker(songId, markerId, {
      noteSheets: { ...marker.noteSheets, [tab]: sheet },
      noteFormats: { ...marker.noteFormats, [tab]: 'sheet' },
    });
  });

  editMarkersModalRoot.addEventListener('focusout', (event) => {
    const cell = (event.target as HTMLElement).closest<HTMLElement>('[data-sheet-row][data-sheet-cell]');
    const editor = cell?.closest<HTMLElement>('.note-sheet-editor');
    if (!cell || !editor) return;
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && editor.contains(nextTarget)) return;
    const songId = editor.dataset.songId!;
    const markerId = editor.dataset.markerId!;
    const tab = editor.dataset.notesTab as MarkerNoteTab;
    const song = appStore.getState().songs.find((item) => item.id === songId);
    const marker = song?.markers.find((item) => item.id === markerId);
    if (!marker) return;
    const rows = markerNoteSheet(marker, tab).rows.map((row) => row.map((item) => ({ ...item })));
    editor.querySelectorAll<HTMLElement>('[data-sheet-row][data-sheet-cell]').forEach((editedCell) => {
      const rowIndex = Number(editedCell.dataset.sheetRow);
      const cellIndex = Number(editedCell.dataset.sheetCell);
      if (rows[rowIndex]?.[cellIndex]) rows[rowIndex][cellIndex].text = editedCell.textContent ?? '';
    });
    // Do not rebuild the modal between pointerdown and click. That was able
    // to replace the sheet beneath the pointer and activate an adjacent
    // marker's controls instead of the cell the user chose.
    window.setTimeout(() => {
      void appStore.updateMarker(songId, markerId, { noteSheets: { ...marker.noteSheets, [tab]: { rows } } });
    }, 0);
  });

  // Enter commits the marker name the same way tabbing/clicking away
  // already does (via the 'change' listener above) and dismisses the
  // on-screen keyboard on mobile — without this, Enter would just insert
  // nothing and the user would have to remember to tap elsewhere first.
  songList.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('marker-label-input') && e.key === 'Enter') {
      target.blur();
    }
  });

  const unsubscribe = appStore.subscribe(() => {
    if (loadedMarkerSongId) {
      const openSong = appStore.getState().songs.find((s) => s.id === loadedMarkerSongId);
      if (openSong) syncSyntheticClick(openSong);
    }
    renderSongs();
    void refreshStorageCard();
  });
  renderSongs();
  void refreshStorageCard();

  // The waveform canvas is sized off its own rendered CSS box — a viewport
  // resize (rotating a tablet, resizing a window) changes that box without
  // going through renderSongs()/appStore at all, so it needs its own redraw.
  const handleResize = (): void => {
    drawMiniWaveform();
    drawBeatAlignmentStrip();
  };
  window.addEventListener('resize', handleResize);

  return () => {
    unsubscribe();
    markerEditorPlayer.teardown();
    window.removeEventListener('resize', handleResize);
  };
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}
